// Idiomas da interface: português (padrão), inglês, espanhol e francês.
//
// Ordem de escolha: ?lang= na URL (atalho para screenshot e links), depois o que ficou
// salvo em localStorage ('agzos.lang'), depois navigator.language; se nada bater, pt.
// Os dicionários são tipados: a chave existe em pt, en, es e fr, senão não compila.
//
// Nos textos de HTML, o valor vindo de fora (nome de agente, projeto) passa por escape()
// antes de entrar em t(): o dicionário pode ter tags (<b>) e não é escapado.

export type Lang = 'pt' | 'en' | 'es' | 'fr';
export type Vars = Record<string, string | number>;

export const IDIOMAS: readonly Lang[] = ['pt', 'en', 'es', 'fr'];

const STORAGE_KEY = 'agzos.lang';
const TAG: Record<Lang, string> = { pt: 'pt-BR', en: 'en-US', es: 'es-ES', fr: 'fr-FR' };

const PT = {
  'topo.ajustes': 'Ajustes',
  'topo.ponte': 'Ponte',
  'ponte.titulo': 'Ponte com as CLIs',
  'ponte.explica': 'Aprovar pedidos e mandar mensagens pelo escritório. Funciona com os plugins agzos-* ligados na CLI. Desligado, nada muda nas sessões.',
  'ponte.aprovar': 'Aprovar pedidos',
  'ponte.mensagens': 'Mandar mensagens',
  'ponte.totp': 'Para aprovar pelo escritório, ative o TOTP no login (--init-secret --totp).',
  'ponte.erro': 'Não deu para salvar: {erro}',
  'ponte.sessoes': '{n} sessões com a caixa de mensagens ativa',
  'det.sem_caixa': 'Esta sessão não está com o plugin agzos-mensagens ativo.',
  'det.desligado': 'Mensagens desligadas para esta CLI (veja Ponte).',
  // login
  'login.sub': 'o escritório dos seus agentes de código',
  'login.senha': 'Senha',
  'login.totp': 'Código do autenticador',
  'login.totp_opcional': '(se houver)',
  'login.usuario': 'Usuário',
  'login.usuario_dica': '(em branco se for o principal)',
  'login.entrar': 'Entrar',
  'login.totp_pedido': 'senha ok — agora o código do autenticador',
  'erro.nao_deu': 'não deu',

  // barra de cima
  'topo.ao_vivo': 'ao vivo',
  'topo.reconectando': 'reconectando…',
  'topo.meu_dia': 'Meu dia',
  'topo.sair': 'Sair',
  'topo.idioma': 'Idioma',
  'num.salas': 'salas',
  'num.agentes': 'agentes',
  'num.trabalhando': 'trabalhando',
  'num.subagentes': 'subagentes',
  'num.precisam': 'precisam de você',
  'conta.sessoes.one': '{n} sessão',
  'conta.sessoes.other': '{n} sessões',
  'conta.erro_leitura': 'erro na leitura',

  // lateral
  'lateral.titulo': 'Escritório',
  'lateral.busca': 'Buscar agente, projeto ou CLI',
  'lateral.vazio': 'Nenhum agente por aqui.',
  'projeto.trabalhando': '{n} de {total} trabalhando',
  'agente.principal': 'principal',
  'agente.sub': 'sub',
  'subs.one': '{n} subagente',
  'subs.other': '{n} subagentes',
  'subs.trabalhando': '{n} trabalhando',

  // status dos agentes
  'status.working': 'trabalhando',
  'status.thinking': 'pensando',
  'status.waiting': 'precisa de você',
  'status.shell': 'esperando o shell',
  'status.idle': 'ocioso',

  // feed
  'feed.titulo': 'Atividade',
  'feed.vazio': 'Nada aconteceu ainda.',
  'feed.alternar': 'Mostrar ou esconder a atividade',

  // avisos e balões
  'aviso.msg': '{nome} {texto} em {projeto}',
  'aviso.precisa': 'precisa de você',
  'acao.fechar': 'Fechar',
  'balao.esperando': 'Esperando você',
  'cartao.subagente': 'subagente',
  'campus.terreo': 'Térreo',
  'campus.andar': '{n}º andar',
  'campus.andar_sigla': 'T',
  'campus.sede': 'Sede {nome}',
  'campus.predio': 'Prédio {nome}',
  'campus.coworking': 'Coworking',
  'area.recepcao': 'Recepção',
  'area.copa': 'Copa',
  'area.lounge': 'Lounge',
  'area.banheiros': 'Banheiros',
  'area.reuniao': 'Sala de reunião',
  'area.servidores': 'Servidores',
  'area.terraco': 'Terraço',

  // detalhe do agente
  'det.agente': 'Agente',
  'det.subagente_de': 'subagente de {nome}',
  'det.sala': 'Sala',
  'det.ha': 'há {tempo}',
  'det.mandar': 'Mandar mensagem',
  'det.placeholder': 'Mensagem para {nome}…',
  'det.enviar': 'Enviar',
  'det.tokens': '{n} tokens nesta sessão',
  'det.subagentes': 'Subagentes',
  'det.linha': 'Linha do tempo',
  'det.sem_atividade': 'Sem atividade recente.',
  'conversa.abrir': 'Ver conversa',
  'conversa.titulo': 'Conversa',
  'conversa.voltar': 'Voltar',
  'conversa.aviso': 'só leitura · segredos mascarados',
  'conversa.carregando': 'Lendo a conversa…',
  'conversa.vazia': 'Nenhuma mensagem nesta sessão ainda.',
  'vivo.aguardando': 'Aguardando o próximo prompt',
  'vivo.assumir': 'Assumir daqui',
  'vivo.buscar': 'Buscar na conversa',
  'vivo.copiar': 'Copiar',
  'vivo.encerrar': 'Encerrar',
  'vivo.encerrar_confirma': 'Encerrar o terminal do escritório de {nome}?',
  'vivo.esperando': 'Esperando você',
  'vivo.maximizar': 'Maximizar',
  'vivo.minimizar': 'Minimizar',
  'vivo.nada_achado': 'Nada encontrado.',
  'vivo.placeholder': 'Mensagem para {nome} (Enter manda, Shift+Enter quebra a linha)',
  'vivo.principal': 'Agente principal',
  'vivo.resumo': 'Esta CLI não deixa ler a conversa: mostrando as ações que o escritório viu.',
  'vivo.saiu': 'A sessão saiu do escritório.',
  'vivo.sala': 'sala {nome}',
  'vivo.sem_ferramentas': 'Sem ferramentas',
  'vivo.so_prompts': 'Só prompts',
  'vivo.subagente': 'Subagente',
  'vivo.titulo': 'terminal',
  'vivo.trabalhando': 'trabalhando',
  'vivo.tudo': 'Tudo',
  'vivo.abrir': 'Abrir terminal',
  'conversa.voce': 'Você',
  'conversa.agente': 'Agente',
  'conversa.ferramenta': 'Ferramenta',
  // terminal (B23/B25)
  'topo.terminal': 'Terminal',
  'det.assumir': 'Assumir no terminal',
  'det.assumir_dica': 'Abre o Claude Code retomando esta sessão numa janela do tmux. Não use os dois ao mesmo tempo.',
  'terminal.titulo': 'Terminal',
  'terminal.explica': 'Sessões tmux abertas pelo escritório (agzos-*). A tela atualiza a cada segundo; segredos saem mascarados.',
  'terminal.novo': 'Novo agente',
  'terminal.cli': 'CLI',
  'terminal.pasta': 'Pasta',
  'terminal.pasta_dica': 'caminho absoluto, dentro de /var/www ou /root',
  'terminal.pedido': 'Primeiro pedido (opcional)',
  'terminal.criar': 'Abrir',
  'terminal.sessoes': 'Sessões',
  'terminal.lista_aria': 'Sessões do terminal',
  'terminal.vazio': 'Nenhuma sessão aberta pelo escritório.',
  'terminal.escolha': 'Escolha uma sessão na lista.',
  'terminal.carregando': 'Lendo a tela…',
  'terminal.tela_vazia': 'Tela vazia.',
  'terminal.tela_aria': 'Tela da sessão',
  'terminal.digite': 'Digite aqui e aperte Enviar',
  'terminal.enter': 'Enter',
  'terminal.ctrl_c': 'Ctrl-C',
  'terminal.encerrar': 'Encerrar',
  'terminal.encerrar_confirma': 'Encerrar esta sessão? O processo para na hora.',
  'terminal.aberto': 'Sessão aberta.',
  'terminal.encerrada': 'Sessão encerrada.',
  'terminal.erro': 'Não deu: {erro}',
  'det.aprovar': 'Aprovar',
  'det.recusar': 'Recusar',
  'perg.titulo': 'Pergunta do agente',
  'perg.responder': 'Responder',
  'perg.recusar': 'Recusar…',
  'perg.recusar_enviar': 'Recusar',
  'perg.motivo': 'Motivo (opcional)',
  'perg.terminal': 'Responder no terminal',
  'perg.terminal_dica': 'Fecha este cartão sem decidir. A pergunta continua no terminal.',
  'perg.outro': 'Outro',
  'perg.outro_ph': 'Escreva a sua resposta',
  'perg.unica': 'Escolha uma opção',
  'perg.multipla': 'Pode escolher mais de uma',
  'det.agente_principal': 'Agente principal',
  'det.sessao': 'Sessão',
  'det.sem_titulo': 'Sessão sem título ainda',
  'det.sala_centraliza': 'Centralizar a sala {nome} no campus',
  'det.seguir': 'Seguir',
  'det.seguir_titulo': 'A câmera acompanha este agente',
  'det.centralizar': 'Centralizar',
  'det.centralizar_titulo': 'Centraliza a câmera neste agente, uma vez',
  'det.terminal_vivo': 'Abrir terminal · ao vivo',
  'det.terminal_vivo_titulo': 'Abre o terminal do escritório desta sessão (tecla T)',
  'det.terminal_sem_sessao': 'Esta sessão não está num terminal do escritório. Use “Assumir no terminal” para abrir uma.',
  'det.agora': 'Agora',
  'det.concluiu': '✅ Concluiu em {duracao}',
  'det.sem_turno': 'Nenhum turno concluído ainda.',
  'det.mandar_dica': 'Enter envia · Shift+Enter quebra a linha',
  'det.nenhum_subagente': 'Nenhum subagente disparado ainda',
  'det.disparados': 'Disparados no total: {n}',
  'det.comando': 'Comando',
  'social.titulo': 'Vida social',
  'social.vazio': 'A vida social deste personagem ainda não começou.',
  'social.moedas': 'moedinhas',
  'social.ganhos': '+{n} ganhos trabalhando',
  'social.vitoria.one': '{n} vitória',
  'social.vitoria.other': '{n} vitórias',
  'social.derrota.one': '{n} derrota',
  'social.derrota.other': '{n} derrotas',
  'social.bordao': 'Bordão: “{texto}”',
  'social.hobbies': 'Hobbies',
  'social.amizades': 'Amizades',
  'social.sem_amigos': 'Nenhuma amizade ainda.',
  'social.rivalidades': 'Rivalidades',
  'social.sem_rivais': 'Nenhuma rivalidade ainda.',
  'social.extrato': 'Extrato',
  'social.extrato_vazio': 'Sem lançamentos ainda.',
  'social.livre': 'Nada no momento',
  'social.fazendo': 'Fazendo agora',
  'tempo.seg': '{n}s',
  'tempo.min_seg': '{m}min {s}s',
  'tempo.h': '{n} h',
  'tempo.d': '{n} d',
  'tempo.agora': 'agora',
  'tempo.min': '{n} min',
  'tempo.h_min': '{h} h {m} min',

  // meu dia
  'dia.agentes.one': 'agente',
  'dia.agentes.other': 'agentes',
  'dia.em': 'em',
  'dia.projetos.one': 'projeto',
  'dia.projetos.other': 'projetos',
  'dia.por_cli': 'Por CLI',
  'dia.tokens': '<b>{n}</b> tokens nas sessões abertas',
  'dia.atividades.one': '{n} atividade registrada',
  'dia.atividades.other': '{n} atividades registradas',
  'dia.anterior': 'Dia anterior',
  'dia.proximo': 'Próximo dia',
  'dia.carregando': 'carregando…',
  'dia.erro': 'não deu para ler este dia',
  'dia.vazio': 'Nada registrado neste dia.',
  'dia.barra': 'Tempo por status',
  'dia.resumo': '<b>{trabalho}</b> trabalhando · <b>{espera}</b> esperando você',
  'dia.projetos': 'Projetos que mais trabalharam',
  'dia.aprovacoes.one': '{n} pedido de aprovação',
  'dia.aprovacoes.other': '{n} pedidos de aprovação',
  'dia.tokens_do_dia': '<b>{n}</b> tokens gastos no dia',
  'dia.timelapse': 'Timelapse',
  'tl.titulo': 'Timelapse do dia',
  'tl.fechar': 'Fechar timelapse',
  'tl.tocar': 'Tocar',
  'tl.pausar': 'Pausar',
  'tl.barra': 'Linha do tempo do dia',
  'tl.velocidade': 'Velocidade',
  'tl.carregando': 'carregando o timelapse…',
  'tl.erro': 'não deu para carregar o timelapse',
  'tl.vazio': 'nada gravado neste dia',
  // barra de ferramentas (toolbar.ts)
  'barra.ferramentas': 'Ferramentas do escritório',
  'barra.grupo_vista': 'Vista',
  'barra.grupo_paineis': 'Painéis',
  'barra.visao': 'Visão geral',
  'barra.afastar': 'Afastar',
  'barra.aproximar': 'Aproximar',
  'barra.entregas': 'Entregas',
  'barra.historico': 'Histórico de sessões',
  'barra.tema': 'Tema da tela',
  'barra.ajuda': 'Ajuda e atalhos',
  'mdia.dia': 'Dia',
  'mdia.dia_aria': 'Escolher o dia',
  'mdia.hoje': 'Hoje',
  'mdia.ontem': 'Ontem',
  'mdia.atualizado': 'Atualizado às {hora} · atualiza a cada 30 s',
  'mdia.esperando': 'Esperando você',
  'mdia.esperas.one': '{n} espera',
  'mdia.esperas.other': '{n} esperas',
  'mdia.maior': 'Maior espera: {duracao} · {nome} em {projeto}, às {hora}',
  'mdia.trabalhando': 'Trabalhando',
  'mdia.trabalhando_sub': 'soma do tempo de todos os agentes',
  'mdia.sessoes': 'Sessões',
  'mdia.subagentes.one': '{n} subagente',
  'mdia.subagentes.other': '{n} subagentes',
  'mdia.pedidos': 'Pedidos',
  'mdia.pedido.one': 'pedido',
  'mdia.pedido.other': 'pedidos',
  'mdia.ferramenta.one': 'ferramenta',
  'mdia.ferramenta.other': 'ferramentas',
  'mdia.concluida.one': 'tarefa concluída',
  'mdia.concluida.other': 'tarefas concluídas',
  'mdia.tokens': 'Tokens',
  'mdia.tokens_sub': 'entrada e saída não separadas pela CLI',
  'mdia.custo': 'Custo',
  'mdia.custo_sem': 'Os transcripts não trouxeram o custo',
  'mdia.legenda': 'Legenda',
  'mdia.leg.esperando': 'Esperando você',
  'mdia.leg.trabalhando': 'Trabalhando',
  'mdia.leg.ocioso': 'Ocioso',
  'mdia.leg.shell': 'Esperando o shell',
  'mdia.por_hora': 'Por hora',
  'mdia.ver_tabela': 'Ver tabela',
  'mdia.ver_grafico': 'Ver gráfico',
  'mdia.horas_aria': 'Tempo dos agentes por hora e status',
  'mdia.tabela_horas': 'Tempo dos agentes por hora e status',
  'mdia.col_hora': 'Hora',
  'mdia.por_projeto': 'Por projeto',
  'mdia.proj_linha': '{espera} esperando você · {total} no total',
  'mdia.tabela_projetos': 'Tempo dos agentes por projeto e status',
  'mdia.col_projeto': 'Projeto',
  'mdia.col_total': 'Total',
  'mdia.maiores_esperas': 'Maiores esperas',
  'mdia.nenhuma_espera': 'Nenhuma espera neste dia.',
  'mdia.agora': 'agora',
  'mdia.motivo.permissao': 'aprovar uma permissão',
  'mdia.motivo.pergunta': 'responder uma pergunta',
  'mdia.motivo.escolha': 'escolher uma opção',
  'mdia.por_conta': 'Por conta',
  'mdia.sessao.one': 'sessão',
  'mdia.sessao.other': 'sessões',
  'mdia.sub': 'sub',
  'mdia.conta_fatia': '{p}% do trabalho',
  'mdia.rodape_1': 'Tempo de agentes é a soma de todos: dois agentes trabalhando por 1 h dão 2 h.',
  'mdia.rodape_2': '“Esperando você” é a mão levantada: permissão, pergunta ou escolha esperando você.',
  'mdia.rodape_3': 'Só conta enquanto o escritório está rodando.',
  'mdia.timelapse': 'Ver timelapse do dia',
  // histórico de sessões (T4)
  'vivo.encerrada': 'Sessão encerrada às {hora}',
  'historico.titulo': 'Histórico de sessões',
  'historico.botao': 'Histórico',
  'historico.explica': 'Últimos 7 dias, de todas as contas. Clique para ler a conversa no terminal.',
  'historico.busca': 'Buscar por título, projeto ou conta',
  'historico.hoje': 'Hoje',
  'historico.ontem': 'Ontem',
  'historico.sem_titulo': 'sem título',
  'historico.aberta': 'aberta',
  'historico.sessoes.one': '{n} sessão',
  'historico.sessoes.other': '{n} sessões',
  'historico.vazio': 'Nenhuma sessão nos últimos 7 dias.',
  'historico.nada': 'Nada achado para esta busca.',
  'historico.carregando': 'carregando o histórico…',
  // entregas (janela: o que cada agente terminou)
  'entregas.botao': 'Entregas',
  'entregas.titulo': 'Entregas',
  'entregas.explica': 'O que cada agente terminou, em poucas palavras. Toque para ver o pedido, a resposta e os anexos.',
  'entregas.vazio': 'Nenhuma entrega ainda. Quando um agente terminar um pedido, ele aparece aqui.',
  'entregas.passos.one': '{n} passo',
  'entregas.passos.other': '{n} passos',
  'entregas.pedido': 'Pedido',
  'entregas.resposta': 'Resposta',
  'entregas.anexos': 'Anexos',
  'entregas.anexo': 'Anexo {n}',
  'entregas.voltar': 'Voltar',
  'entregas.abrir_imagem': 'Abrir a imagem em outra aba',
  'entregas.sem_pedido': 'Pedido não registrado.',
  'entregas.sumiu': 'Esta entrega saiu do registro.',
  'entregas.erro': 'Não deu para carregar as entregas.',
  // PWA (pwa.ts): instalar, versão nova e sem conexão
  'pwa.instalar': 'Instalar',
  'pwa.instalar_texto': 'Instale o escritório na tela inicial: abre em tela cheia, como um app.',
  'pwa.nova_versao': 'Nova versão disponível',
  'pwa.atualizar': 'Atualizar',
  'pwa.sem_conexao': 'Sem conexão com o escritório. Os dados voltam quando o servidor responder.',
  'pwa.tentar': 'Tentar de novo',
  'pwa.ios': 'Para instalar: toque em Compartilhar e depois em “Adicionar à Tela de Início”.',
  'pwa.agora_nao': 'Agora não',
  // celular (mobile.ts): HUD, abas e bottom sheets
  'jogo.abas': 'Abas do jogo',
  'jogo.aba.escritorio': 'Escritório',
  'jogo.aba.agentes': 'Agentes',
  'jogo.aba.entregas': 'Entregas',
  'jogo.aba.dia': 'Meu dia',
  'jogo.aba.mais': 'Mais',
  'jogo.ultima': 'última atividade',
  'jogo.sem_atividade': 'nada ainda',
  'jogo.consumo': 'Consumo do plano',
  'jogo.consumo_vazio': 'Nenhuma CLI informou o consumo ainda.',
  'jogo.arrastar': 'Arrastar para mudar a altura ou fechar',
  'vivo.voltar': 'Voltar',
  'vivo.menu': 'Mais opções',
  'vivo.filtros': 'Filtros',
  'missao.titulo': 'Precisa de você',
  'missao.de': '{n} de {total}',
  'missao.so_admin': 'Só um admin pode decidir. Aqui você vê como aviso.',
  'missao.entendi': 'Entendi',
  // notificações no celular (B29-M5): painel do Mais e do CMS
  'push.item': 'Notificações',
  'push.ativar': 'Ativar notificações neste aparelho',
  'push.desativar': 'Desativar neste aparelho',
  'push.testar': 'Enviar teste',
  'push.teste_ok': 'Teste enviado. Deve chegar em instantes.',
  'push.teste_sem_aparelho': 'Ative as notificações neste aparelho antes do teste.',
  'push.teste_falhou': 'O teste não chegou a nenhum aparelho.',
  'push.ativo': 'Este aparelho recebe notificações.',
  'push.inativo': 'Este aparelho não recebe notificações.',
  'push.sem_suporte': 'Este navegador não recebe notificações do escritório.',
  'push.ios': 'No iPhone e no iPad, as notificações só funcionam com o app instalado na Tela de Início (iOS 16.4 ou mais novo). Abra pelo botão Compartilhar, “Adicionar à Tela de Início”, e ative por lá.',
  'push.negada': 'A permissão de notificações foi negada. Libere nas configurações do navegador.',
  'push.falhou': 'Não deu para concluir. Tente de novo.',
  'push.quando': 'Avisar quando',
  'push.precisa': 'Alguém precisa de você',
  'push.concluiu': 'Um agente concluir',
  'push.aparelhos': 'Aparelhos inscritos',
  'push.nenhum': 'Nenhum aparelho inscrito.',
  'push.remover': 'remover',
  'push.este': 'este aparelho',
  'push.salvo': 'Salvo.',
  // topo: contador de shells (5.1)
  'num.shells': 'shells rodando',
  'num.shells_dica': 'Clique para ir ao próximo agente com shell rodando',
  'sons.titulo': 'Sons',
  'sons.dica': 'Desligados por padrão. Sons baixinhos, feitos no navegador. Com a aba oculta, só o sino toca.',
  'sons.volume': 'Volume',
  'sons.sino': 'Sino: alguém precisa de você',
  'sons.teclado': 'Teclado: quem trabalha nas salas visíveis',
  'sons.elevador': 'Elevador: alguém chega ou sai',
  'sons.concluida': 'Estalo: tarefa concluída',
  'sons.rodas': 'Rodas: pingue-pongue, fliperama, jokenpô',
  'sons.fechar': 'Fechar',
  // demonstração pela interface (demo.ts)
  'demo.selo': 'demonstração',
  'demo.selo_titulo': 'Agentes fictícios para ver o escritório cheio. Não entram no Meu dia.',
  'demo.ver': 'Ver demonstração',
  'demo.faixa': 'agentes fictícios junto com os reais',
  'demo.desligar': 'Desligar',
  'demo.erro': 'Não deu para mudar a demonstração: {erro}',
  'demo.sem_resposta': 'sem resposta do servidor',
  'demo.painel': 'Agente fictício: não é uma sessão real.',
  // editor de personagem (ed.*) e o lápis do painel
  'det.editar_personagem': 'Editar personagem',
  'ed.titulo_projeto': 'Personagem do agente principal de {projeto}',
  'ed.titulo_sessao': 'Personagem de {nome} ({projeto})',
  'ed.fechar': 'Fechar',
  'ed.previa': 'Prévia do personagem',
  'ed.sortear': 'Sortear',
  'ed.voltar_sorteio': 'Voltar ao sorteio',
  'ed.salvar': 'Salvar',
  'ed.salvando': 'Salvando…',
  'ed.cancelar': 'Cancelar',
  'ed.salvo': 'Personagem salvo.',
  'ed.erro': 'Não deu para salvar: {erro}',
  'ed.nome': 'Nome',
  'ed.nome_dica': 'De 1 a {max} letras, sem repetir o nome de quem está no escritório, de outro personagem ou de outro projeto.',
  'ed.nome_vazio': 'Escreva um nome.',
  'ed.nome_longo': 'Use até {max} letras.',
  'ed.nome_repetido': 'Esse nome já está em uso.',
  'ed.grupo_corpo': 'Corpo',
  'ed.grupo_pele': 'Pele',
  'ed.grupo_cabelo': 'Cabelo',
  'ed.grupo_cabelo_cor': 'Cor do cabelo',
  'ed.grupo_barba': 'Barba',
  'ed.grupo_olhos': 'Olhos',
  'ed.grupo_acessorio': 'Acessório',
  'ed.grupo_acessorio_cor': 'Cor do acessório',
  'ed.grupo_cima': 'Parte de cima',
  'ed.grupo_cima_cor': 'Cor da parte de cima',
  'ed.grupo_baixo': 'Parte de baixo',
  'ed.grupo_sapatos': 'Sapatos',
  'ed.nenhum': 'Nenhum',
  'ed.cor': 'Cor {n}',
  'ed.peca_dica': 'O escritório desenha um acessório por personagem: óculos, barba e acessório se trocam.',
  'ed.editar_pecas': 'Editar peças',
  'det.estatisticas': 'Estatísticas',
  'stats.ferramentas': 'Ferramentas',
  'stats.chamadas.one': '{n} chamada',
  'stats.chamadas.other': '{n} chamadas',
  'stats.entrada': 'Entrada',
  'stats.saida': 'Saída',
  'stats.custo': 'Custo',
  'stats.custo_sem': 'A CLI não gravou o custo desta sessão',
  'stats.custo_sub': 'soma das sessões que informam o custo',
  'stats.linhas': 'Linhas alteradas',
  'stats.modelo': 'Modelo',
  'stats.branch': 'Branch',
  'stats.entrada_saida': 'entrada {e} · saída {s}',
  'stats.desatualizado': 'desatualizado',
  'stats.desatualizado_titulo': 'A leitura do consumo tem mais de 30 min e pode estar antiga',
  'stats.sem_cota': 'sem cota',
  'stats.sem_cota_titulo': 'O Codex informa crédito zero: sem cota agora',
  'mdia.leg_alterna': 'Clique para mostrar ou esconder este status nos gráficos',
  'gh.t.pr-open': '🎉 abriu o PR{n}',
  'gh.t.pr-merge': '🎉 mergeou o PR{n}',
  'gh.t.release': '🎉 publicou a release{tag}',
  'gh.t.ci-fail': '🚨 CI falhou{ramo}',
  'gh.t.ci-pass': '✅ CI verde{ramo}',
  'gh.t.push': '⬆️ fez push{ramo}',
  'gh.b.pr-open': 'PR{n} aberto!',
  'gh.b.pr-merge': 'PR{n} mergeado!',
  'gh.b.release': 'Release{tag} publicada!',
  'gh.b.ci-pass': 'CI verde!',
  'gh.alarme': '🚨 CI falhou',
  'pty.titulo': 'Terminal interativo',
  'pty.nova': 'Nova sessão',
  'pty.cli': 'CLI',
  'pty.conta': 'Conta',
  'pty.pasta': 'Pasta do projeto',
  'pty.pedido': 'Primeiro pedido (opcional)',
  'pty.criar': 'Abrir',
  'pty.encerrar': 'Encerrar sessão',
  'pty.confirmar': 'Clique de novo para encerrar',
  'pty.assumir_confirma': 'Isto ENCERRA a sessão aberta no outro terminal e continua a mesma conversa aqui. Continuar?',
  'pty.vazio': 'Nenhuma sessão aberta. Abra uma em “Nova sessão”.',
  'pty.conectando': 'Conectando…',
  'pty.terminou': '[o processo terminou]',
  'pty.sem_pty': 'O terminal interativo precisa do node-pty, que não está instalado nesta máquina. O terminal por tmux continua valendo.',
  'pty.fechar_aba': 'Fechar',
  'pty.teclas': 'Teclas',
  'pty.colar': 'Colar',
  'pty.aba_aria': 'Sessões do terminal interativo',
  'pty.sem_conta': 'Conta padrão',
  // aprovações e mensagens avançadas (5.3)
  'perm.sempre': 'Aprovar e não perguntar de novo',
  'perm.interromper': 'interromper o agente',
  'perm.interromper_dica': 'Recusa e para o agente agora, em vez de só recusar esta ação.',
  'msg.na_fila': 'na fila',
  'msg.entregue': 'entregue',
  'msg.nao_entregue': 'não entregue: {motivo}',
  'ponte.codex_fila': 'O Codex ainda não tem fila de mensagens nesta versão (o codex não tem o subcomando queue), então a caixa segue desligada.',
  'balao.concluido': 'Concluído',
  // timelapse completo (7.2): velocidades, picos, replay e volta ao vivo (timelapse.ts)
  'tl.voltar': 'Voltar ao vivo',
  'tl.replay': 'REPLAY',
  'tl.picos': 'Picos do dia',
  'tl.pico': 'Pico às {h}: {n} trabalhando',
  'tl.resumo': '{p} no campus · {n} trabalhando',
  'tl.legenda_presentes': 'presentes',
  'tl.legenda_trabalho': 'trabalhando',
  'mdia.demo_aviso': 'Números fictícios: não são trabalho real e nada disto é gravado.',
  'roda.tv': 'TV',
  'roda.videogame': 'Videogame',
  'roda.fliperama': 'Fliperama',
  'roda.pingpong': 'Pingue-pongue',
  'roda.papo': 'Papo na copa',
  'roda.corredor': 'Conversa em pé',
  'roda.jokenpo': 'Jokenpô',
  'roda.espelho': 'Espelho do banheiro',
  'roda.prog.futebol': 'Futebol',
  'roda.prog.novela': 'Novela',
  'roda.prog.desenho': 'Desenho',
  'roda.gol': '⚽ GOL!',
  'roda.fofoca.0': '🤫 Viu o {nome}?',
  'roda.fofoca.1': '🤭 O {nome} sumiu…',
  'roda.fofoca.2': '👀 Ouvi do {nome}…',
  'roda.fofoca.3': '😏 E o {nome}, hein?',
  'traco.falante': 'Falante',
  'traco.competitivo': 'Competitivo',
  'traco.caseiro': 'Caseiro',
  'traco.gamer': 'Gamer',
  'traco.cinefilo': 'Cinéfilo',
  'traco.fofoqueiro': 'Fofoqueiro',
  'traco.atleta': 'Atleta',
  'traco.vaidoso': 'Vaidoso',
  'traco.cafeinado': 'Cafeinado',
  'traco.brincalhao': 'Brincalhão',
  'traco.sossegado': 'Sossegado',
  'traco.retro': 'Retrô',
  'traco.sociavel': 'Sociável',
  'traco.curioso': 'Curioso',
  'roda.traco': 'Personalidade',
} as const;

export type Chave = keyof typeof PT;

const EN: Record<Chave, string> = {
  'topo.ajustes': 'Settings',
  'topo.ponte': 'Bridge',
  'ponte.titulo': 'CLI bridge',
  'ponte.explica': 'Approve requests and send messages from the office. Works with the agzos-* plugins enabled in the CLI. When off, sessions are untouched.',
  'ponte.aprovar': 'Approve requests',
  'ponte.mensagens': 'Send messages',
  'ponte.totp': 'To approve from the office, enable TOTP on login (--init-secret --totp).',
  'ponte.erro': 'Could not save: {erro}',
  'ponte.sessoes': '{n} sessions with the inbox active',
  'det.sem_caixa': 'This session does not have the agzos-mensagens plugin active.',
  'det.desligado': 'Messages are off for this CLI (see Bridge).',
  'login.sub': 'the office for your coding agents',
  'login.senha': 'Password',
  'login.totp': 'Authenticator code',
  'login.totp_opcional': '(if any)',
  'login.usuario': 'Username',
  'login.usuario_dica': '(blank for the main admin)',
  'login.entrar': 'Log in',
  'login.totp_pedido': 'password ok — now the authenticator code',
  'erro.nao_deu': "didn't work",

  'topo.ao_vivo': 'live',
  'topo.reconectando': 'reconnecting…',
  'topo.meu_dia': 'My day',
  'topo.sair': 'Log out',
  'topo.idioma': 'Language',
  'num.salas': 'rooms',
  'num.agentes': 'agents',
  'num.trabalhando': 'working',
  'num.subagentes': 'subagents',
  'num.precisam': 'need you',
  'conta.sessoes.one': '{n} session',
  'conta.sessoes.other': '{n} sessions',
  'conta.erro_leitura': 'read error',

  'lateral.titulo': 'Office',
  'lateral.busca': 'Search agent, project or CLI',
  'lateral.vazio': 'No agents here.',
  'projeto.trabalhando': '{n} of {total} working',
  'agente.principal': 'main',
  'agente.sub': 'sub',
  'subs.one': '{n} subagent',
  'subs.other': '{n} subagents',
  'subs.trabalhando': '{n} working',

  'status.working': 'working',
  'status.thinking': 'thinking',
  'status.waiting': 'needs you',
  'status.shell': 'waiting for shell',
  'status.idle': 'idle',

  'feed.titulo': 'Activity',
  'feed.vazio': 'Nothing has happened yet.',
  'feed.alternar': 'Show or hide activity',

  'aviso.msg': '{nome} {texto} in {projeto}',
  'aviso.precisa': 'needs you',
  'acao.fechar': 'Close',
  'balao.esperando': 'Waiting for you',
  'cartao.subagente': 'subagent',
  'campus.terreo': 'Ground floor',
  'campus.andar': 'Floor {n}',
  'campus.andar_sigla': 'G',
  'campus.sede': '{nome} HQ',
  'campus.predio': '{nome} building',
  'campus.coworking': 'Coworking',
  'area.recepcao': 'Reception',
  'area.copa': 'Pantry',
  'area.lounge': 'Lounge',
  'area.banheiros': 'Restrooms',
  'area.reuniao': 'Meeting room',
  'area.servidores': 'Servers',
  'area.terraco': 'Terrace',

  'det.agente': 'Agent',
  'det.subagente_de': 'subagent of {nome}',
  'det.sala': 'Room',
  'det.ha': '{tempo} ago',
  'det.mandar': 'Send message',
  'det.placeholder': 'Message to {nome}…',
  'det.enviar': 'Send',
  'det.tokens': '{n} tokens in this session',
  'det.subagentes': 'Subagents',
  'det.linha': 'Timeline',
  'det.sem_atividade': 'No recent activity.',
  'conversa.abrir': 'View conversation',
  'conversa.titulo': 'Conversation',
  'conversa.voltar': 'Back',
  'conversa.aviso': 'read only · secrets masked',
  'conversa.carregando': 'Reading the conversation…',
  'conversa.vazia': 'No messages in this session yet.',
  'vivo.aguardando': 'Waiting for the next prompt',
  'vivo.assumir': 'Take over here',
  'vivo.buscar': 'Search the conversation',
  'vivo.copiar': 'Copy',
  'vivo.encerrar': 'Stop',
  'vivo.encerrar_confirma': 'Stop {nome}’s office terminal?',
  'vivo.esperando': 'Waiting for you',
  'vivo.maximizar': 'Maximize',
  'vivo.minimizar': 'Minimize',
  'vivo.nada_achado': 'Nothing found.',
  'vivo.placeholder': 'Message to {nome} (Enter sends, Shift+Enter new line)',
  'vivo.principal': 'Main agent',
  'vivo.resumo': 'This CLI does not expose its conversation: showing the actions the office saw.',
  'vivo.saiu': 'The session left the office.',
  'vivo.sala': 'room {nome}',
  'vivo.sem_ferramentas': 'No tools',
  'vivo.so_prompts': 'Prompts only',
  'vivo.subagente': 'Subagent',
  'vivo.titulo': 'terminal',
  'vivo.trabalhando': 'working',
  'vivo.tudo': 'All',
  'vivo.abrir': 'Open terminal',
  'conversa.voce': 'You',
  'conversa.agente': 'Agent',
  'conversa.ferramenta': 'Tool',
  // terminal (B23/B25)
  'topo.terminal': 'Terminal',
  'det.assumir': 'Take over in terminal',
  'det.assumir_dica': 'Opens Claude Code resuming this session in a tmux window. Don’t use both at the same time.',
  'terminal.titulo': 'Terminal',
  'terminal.explica': 'tmux sessions opened by the office (agzos-*). The screen refreshes every second; secrets are masked.',
  'terminal.novo': 'New agent',
  'terminal.cli': 'CLI',
  'terminal.pasta': 'Folder',
  'terminal.pasta_dica': 'absolute path, inside /var/www or /root',
  'terminal.pedido': 'First request (optional)',
  'terminal.criar': 'Open',
  'terminal.sessoes': 'Sessions',
  'terminal.lista_aria': 'Terminal sessions',
  'terminal.vazio': 'No session opened by the office.',
  'terminal.escolha': 'Pick a session from the list.',
  'terminal.carregando': 'Reading the screen…',
  'terminal.tela_vazia': 'Empty screen.',
  'terminal.tela_aria': 'Session screen',
  'terminal.digite': 'Type here and press Send',
  'terminal.enter': 'Enter',
  'terminal.ctrl_c': 'Ctrl-C',
  'terminal.encerrar': 'End',
  'terminal.encerrar_confirma': 'End this session? The process stops right away.',
  'terminal.aberto': 'Session opened.',
  'terminal.encerrada': 'Session ended.',
  'terminal.erro': 'Didn\'t work: {erro}',
  'det.aprovar': 'Approve',
  'det.recusar': 'Deny',
  'perg.titulo': 'Agent question',
  'perg.responder': 'Answer',
  'perg.recusar': 'Decline…',
  'perg.recusar_enviar': 'Decline',
  'perg.motivo': 'Reason (optional)',
  'perg.terminal': 'Answer in the terminal',
  'perg.terminal_dica': 'Closes this card without answering. The question stays in the terminal.',
  'perg.outro': 'Other',
  'perg.outro_ph': 'Write your answer',
  'perg.unica': 'Pick one option',
  'perg.multipla': 'You can pick more than one',
  'det.agente_principal': 'Main agent',
  'det.sessao': 'Session',
  'det.sem_titulo': 'Untitled session so far',
  'det.sala_centraliza': 'Center the {nome} room on the campus',
  'det.seguir': 'Follow',
  'det.seguir_titulo': 'The camera follows this agent',
  'det.centralizar': 'Center',
  'det.centralizar_titulo': 'Centers the camera on this agent, once',
  'det.terminal_vivo': 'Open terminal · live',
  'det.terminal_vivo_titulo': 'Opens this session’s office terminal (key T)',
  'det.terminal_sem_sessao': 'This session is not in an office terminal. Use “Take over in terminal” to open one.',
  'det.agora': 'Now',
  'det.concluiu': '✅ Finished in {duracao}',
  'det.sem_turno': 'No turn finished yet.',
  'det.mandar_dica': 'Enter sends · Shift+Enter adds a line',
  'det.nenhum_subagente': 'No subagent started yet',
  'det.disparados': 'Started in total: {n}',
  'det.comando': 'Command',
  'social.titulo': 'Social life',
  'social.vazio': 'This character’s social life hasn’t started yet.',
  'social.moedas': 'coins',
  'social.ganhos': '+{n} earned working',
  'social.vitoria.one': '{n} win',
  'social.vitoria.other': '{n} wins',
  'social.derrota.one': '{n} loss',
  'social.derrota.other': '{n} losses',
  'social.bordao': 'Catchphrase: “{texto}”',
  'social.hobbies': 'Hobbies',
  'social.amizades': 'Friends',
  'social.sem_amigos': 'No friends yet.',
  'social.rivalidades': 'Rivalries',
  'social.sem_rivais': 'No rivalries yet.',
  'social.extrato': 'Statement',
  'social.extrato_vazio': 'No entries yet.',
  'social.livre': 'Nothing right now',
  'social.fazendo': 'Doing now',
  'tempo.seg': '{n}s',
  'tempo.min_seg': '{m}min {s}s',
  'tempo.h': '{n} h',
  'tempo.d': '{n} d',
  'tempo.agora': 'now',
  'tempo.min': '{n} min',
  'tempo.h_min': '{h} h {m} min',

  'dia.agentes.one': 'agent',
  'dia.agentes.other': 'agents',
  'dia.em': 'in',
  'dia.projetos.one': 'project',
  'dia.projetos.other': 'projects',
  'dia.por_cli': 'By CLI',
  'dia.tokens': '<b>{n}</b> tokens in open sessions',
  'dia.atividades.one': '{n} activity recorded',
  'dia.atividades.other': '{n} activities recorded',
  'dia.anterior': 'Previous day',
  'dia.proximo': 'Next day',
  'dia.carregando': 'loading…',
  'dia.erro': 'could not read this day',
  'dia.vazio': 'Nothing recorded on this day.',
  'dia.barra': 'Time by status',
  'dia.resumo': '<b>{trabalho}</b> working · <b>{espera}</b> waiting for you',
  'dia.projetos': 'Projects that worked the most',
  'dia.aprovacoes.one': '{n} approval request',
  'dia.aprovacoes.other': '{n} approval requests',
  'dia.tokens_do_dia': '<b>{n}</b> tokens spent on this day',
  'dia.timelapse': 'Timelapse',
  'tl.titulo': 'Timelapse of the day',
  'tl.fechar': 'Close timelapse',
  'tl.tocar': 'Play',
  'tl.pausar': 'Pause',
  'tl.barra': 'Timeline of the day',
  'tl.velocidade': 'Speed',
  'tl.carregando': 'loading the timelapse…',
  'tl.erro': 'could not load the timelapse',
  'tl.vazio': 'nothing recorded on this day',
  // barra de ferramentas (toolbar.ts)
  'barra.ferramentas': 'Office tools',
  'barra.grupo_vista': 'View',
  'barra.grupo_paineis': 'Panels',
  'barra.visao': 'Overview',
  'barra.afastar': 'Zoom out',
  'barra.aproximar': 'Zoom in',
  'barra.entregas': 'Deliveries',
  'barra.historico': 'Session history',
  'barra.tema': 'Screen theme',
  'barra.ajuda': 'Help and shortcuts',
  'mdia.dia': 'Day',
  'mdia.dia_aria': 'Choose the day',
  'mdia.hoje': 'Today',
  'mdia.ontem': 'Yesterday',
  'mdia.atualizado': 'Updated at {hora} · refreshes every 30 s',
  'mdia.esperando': 'Waiting for you',
  'mdia.esperas.one': '{n} wait',
  'mdia.esperas.other': '{n} waits',
  'mdia.maior': 'Longest wait: {duracao} · {nome} in {projeto}, at {hora}',
  'mdia.trabalhando': 'Working',
  'mdia.trabalhando_sub': 'sum of time across all agents',
  'mdia.sessoes': 'Sessions',
  'mdia.subagentes.one': '{n} subagent',
  'mdia.subagentes.other': '{n} subagents',
  'mdia.pedidos': 'Requests',
  'mdia.pedido.one': 'request',
  'mdia.pedido.other': 'requests',
  'mdia.ferramenta.one': 'tool',
  'mdia.ferramenta.other': 'tools',
  'mdia.concluida.one': 'task done',
  'mdia.concluida.other': 'tasks done',
  'mdia.tokens': 'Tokens',
  'mdia.tokens_sub': 'input and output not split by the CLI',
  'mdia.custo': 'Cost',
  'mdia.custo_sem': 'The transcripts did not bring the cost',
  'mdia.legenda': 'Legend',
  'mdia.leg.esperando': 'Waiting for you',
  'mdia.leg.trabalhando': 'Working',
  'mdia.leg.ocioso': 'Idle',
  'mdia.leg.shell': 'Waiting on shell',
  'mdia.por_hora': 'By hour',
  'mdia.ver_tabela': 'View table',
  'mdia.ver_grafico': 'View chart',
  'mdia.horas_aria': 'Agent time by hour and status',
  'mdia.tabela_horas': 'Agent time by hour and status',
  'mdia.col_hora': 'Hour',
  'mdia.por_projeto': 'By project',
  'mdia.proj_linha': '{espera} waiting for you · {total} total',
  'mdia.tabela_projetos': 'Agent time by project and status',
  'mdia.col_projeto': 'Project',
  'mdia.col_total': 'Total',
  'mdia.maiores_esperas': 'Longest waits',
  'mdia.nenhuma_espera': 'No waits on this day.',
  'mdia.agora': 'now',
  'mdia.motivo.permissao': 'approve a permission',
  'mdia.motivo.pergunta': 'answer a question',
  'mdia.motivo.escolha': 'choose an option',
  'mdia.por_conta': 'By account',
  'mdia.sessao.one': 'session',
  'mdia.sessao.other': 'sessions',
  'mdia.sub': 'sub',
  'mdia.conta_fatia': '{p}% of the work',
  'mdia.rodape_1': 'Agent time is the sum of all of them: two agents working for 1 h make 2 h.',
  'mdia.rodape_2': '“Waiting for you” is a raised hand: a permission, question or choice waiting for you.',
  'mdia.rodape_3': 'Only counts while the office is running.',
  'mdia.timelapse': 'View the day\'s timelapse',
  // histórico de sessões (T4)
  'vivo.encerrada': 'Session ended at {hora}',
  'historico.titulo': 'Session history',
  'historico.botao': 'History',
  'historico.explica': 'Last 7 days, from all accounts. Click to read the conversation in the terminal.',
  'historico.busca': 'Search by title, project or account',
  'historico.hoje': 'Today',
  'historico.ontem': 'Yesterday',
  'historico.sem_titulo': 'untitled',
  'historico.aberta': 'open',
  'historico.sessoes.one': '{n} session',
  'historico.sessoes.other': '{n} sessions',
  'historico.vazio': 'No session in the last 7 days.',
  'historico.nada': 'Nothing found for this search.',
  'historico.carregando': 'loading the history…',
  // entregas (janela: what each agent finished)
  'entregas.botao': 'Deliveries',
  'entregas.titulo': 'Deliveries',
  'entregas.explica': 'What each agent finished, in a few words. Tap to see the request, the answer and the attachments.',
  'entregas.vazio': 'No deliveries yet. When an agent finishes a request, it shows up here.',
  'entregas.passos.one': '{n} step',
  'entregas.passos.other': '{n} steps',
  'entregas.pedido': 'Request',
  'entregas.resposta': 'Answer',
  'entregas.anexos': 'Attachments',
  'entregas.anexo': 'Attachment {n}',
  'entregas.voltar': 'Back',
  'entregas.abrir_imagem': 'Open the image in a new tab',
  'entregas.sem_pedido': 'Request not recorded.',
  'entregas.sumiu': 'This delivery is no longer in the record.',
  'entregas.erro': 'Could not load the deliveries.',
  // PWA (pwa.ts): install, new version and offline
  'pwa.instalar': 'Install',
  'pwa.instalar_texto': 'Install the office on your home screen: it opens full screen, like an app.',
  'pwa.nova_versao': 'New version available',
  'pwa.atualizar': 'Update',
  'pwa.sem_conexao': 'No connection to the office. Data comes back when the server responds.',
  'pwa.tentar': 'Try again',
  'pwa.ios': 'To install: tap Share and then “Add to Home Screen”.',
  'pwa.agora_nao': 'Not now',
  // mobile (mobile.ts): HUD, tabs and bottom sheets
  'jogo.abas': 'Game tabs',
  'jogo.aba.escritorio': 'Office',
  'jogo.aba.agentes': 'Agents',
  'jogo.aba.entregas': 'Deliveries',
  'jogo.aba.dia': 'My day',
  'jogo.aba.mais': 'More',
  'jogo.ultima': 'last activity',
  'jogo.sem_atividade': 'nothing yet',
  'jogo.consumo': 'Plan usage',
  'jogo.consumo_vazio': 'No CLI has reported usage yet.',
  'jogo.arrastar': 'Drag to change the height or close',
  'vivo.voltar': 'Back',
  'vivo.menu': 'More options',
  'vivo.filtros': 'Filters',
  'missao.titulo': 'Needs you',
  'missao.de': '{n} of {total}',
  'missao.so_admin': 'Only an admin can decide. You are seeing this as a notice.',
  'missao.entendi': 'Got it',
  // notificações no celular (B29-M5): painel do Mais e do CMS
  'push.item': 'Notifications',
  'push.ativar': 'Turn on notifications on this device',
  'push.desativar': 'Turn off on this device',
  'push.testar': 'Send test',
  'push.teste_ok': 'Test sent. It should arrive shortly.',
  'push.teste_sem_aparelho': 'Turn on notifications on this device before testing.',
  'push.teste_falhou': 'The test did not reach any device.',
  'push.ativo': 'This device receives notifications.',
  'push.inativo': 'This device does not receive notifications.',
  'push.sem_suporte': 'This browser cannot receive office notifications.',
  'push.ios': 'On iPhone and iPad, notifications only work with the app installed on the Home Screen (iOS 16.4 or later). Open the Share button, “Add to Home Screen”, and turn them on from there.',
  'push.negada': 'Notification permission was denied. Allow it in the browser settings.',
  'push.falhou': 'Could not finish. Try again.',
  'push.quando': 'Notify me when',
  'push.precisa': 'Someone needs you',
  'push.concluiu': 'An agent finishes',
  'push.aparelhos': 'Subscribed devices',
  'push.nenhum': 'No devices subscribed.',
  'push.remover': 'remove',
  'push.este': 'this device',
  'push.salvo': 'Saved.',
  // topo: contador de shells (5.1)
  'num.shells': 'shells running',
  'num.shells_dica': 'Click to go to the next agent with a shell running',
  'sons.titulo': 'Sounds',
  'sons.dica': 'Off by default. Quiet sounds, made in the browser. With the tab hidden, only the bell rings.',
  'sons.volume': 'Volume',
  'sons.sino': 'Bell: someone needs you',
  'sons.teclado': 'Keyboard: people typing in the rooms on screen',
  'sons.elevador': 'Elevator: someone arrives or leaves',
  'sons.concluida': 'Pop: task done',
  'sons.rodas': 'Games: ping-pong, arcade, rock-paper-scissors',
  'sons.fechar': 'Close',
  // demonstração pela interface (demo.ts)
  'demo.selo': 'demo',
  'demo.selo_titulo': 'Made-up agents to fill the office. They do not count in My day.',
  'demo.ver': 'View demo',
  'demo.faixa': 'made-up agents next to the real ones',
  'demo.desligar': 'Turn off',
  'demo.erro': 'Could not change the demo: {erro}',
  'demo.sem_resposta': 'no answer from the server',
  'demo.painel': 'Made-up agent: not a real session.',
  // character editor (ed.*) and the pencil in the panel
  'det.editar_personagem': 'Edit character',
  'ed.titulo_projeto': 'Character of the main agent of {projeto}',
  'ed.titulo_sessao': 'Character of {nome} ({projeto})',
  'ed.fechar': 'Close',
  'ed.previa': 'Character preview',
  'ed.sortear': 'Shuffle',
  'ed.voltar_sorteio': 'Reset to the draw',
  'ed.salvar': 'Save',
  'ed.salvando': 'Saving…',
  'ed.cancelar': 'Cancel',
  'ed.salvo': 'Character saved.',
  'ed.erro': 'Could not save: {erro}',
  'ed.nome': 'Name',
  'ed.nome_dica': 'From 1 to {max} letters, not repeating the name of someone in the office, of another character or of another project.',
  'ed.nome_vazio': 'Write a name.',
  'ed.nome_longo': 'Use up to {max} letters.',
  'ed.nome_repetido': 'That name is already in use.',
  'ed.grupo_corpo': 'Body',
  'ed.grupo_pele': 'Skin',
  'ed.grupo_cabelo': 'Hair',
  'ed.grupo_cabelo_cor': 'Hair color',
  'ed.grupo_barba': 'Beard',
  'ed.grupo_olhos': 'Eyes',
  'ed.grupo_acessorio': 'Accessory',
  'ed.grupo_acessorio_cor': 'Accessory color',
  'ed.grupo_cima': 'Top',
  'ed.grupo_cima_cor': 'Top color',
  'ed.grupo_baixo': 'Bottom',
  'ed.grupo_sapatos': 'Shoes',
  'ed.nenhum': 'None',
  'ed.cor': 'Color {n}',
  'ed.peca_dica': 'The office draws one accessory per character: glasses, beard and accessory replace each other.',
  'ed.editar_pecas': 'Edit parts',
  'det.estatisticas': 'Statistics',
  'stats.ferramentas': 'Tools',
  'stats.chamadas.one': '{n} call',
  'stats.chamadas.other': '{n} calls',
  'stats.entrada': 'Input',
  'stats.saida': 'Output',
  'stats.custo': 'Cost',
  'stats.custo_sem': 'The CLI did not record this session’s cost',
  'stats.custo_sub': 'sum of the sessions that report cost',
  'stats.linhas': 'Lines changed',
  'stats.modelo': 'Model',
  'stats.branch': 'Branch',
  'stats.entrada_saida': 'input {e} · output {s}',
  'stats.desatualizado': 'outdated',
  'stats.desatualizado_titulo': 'The usage reading is over 30 min old and may be stale',
  'stats.sem_cota': 'no quota',
  'stats.sem_cota_titulo': 'Codex reports zero credit: no quota right now',
  'mdia.leg_alterna': 'Click to show or hide this status in the charts',
  'gh.t.pr-open': '🎉 opened PR{n}',
  'gh.t.pr-merge': '🎉 merged PR{n}',
  'gh.t.release': '🎉 published release{tag}',
  'gh.t.ci-fail': '🚨 CI failed{ramo}',
  'gh.t.ci-pass': '✅ CI is green{ramo}',
  'gh.t.push': '⬆️ pushed{ramo}',
  'gh.b.pr-open': 'PR{n} opened!',
  'gh.b.pr-merge': 'PR{n} merged!',
  'gh.b.release': 'Release{tag} published!',
  'gh.b.ci-pass': 'CI is green!',
  'gh.alarme': '🚨 CI failed',
  'pty.titulo': 'Interactive terminal',
  'pty.nova': 'New session',
  'pty.cli': 'CLI',
  'pty.conta': 'Account',
  'pty.pasta': 'Project folder',
  'pty.pedido': 'First request (optional)',
  'pty.criar': 'Open',
  'pty.encerrar': 'End session',
  'pty.confirmar': 'Click again to end',
  'pty.assumir_confirma': 'This ENDS the session open in the other terminal and continues the same conversation here. Continue?',
  'pty.vazio': 'No open session. Start one in “New session”.',
  'pty.conectando': 'Connecting…',
  'pty.terminou': '[the process has ended]',
  'pty.sem_pty': 'The interactive terminal needs node-pty, which is not installed on this machine. The tmux terminal still works.',
  'pty.fechar_aba': 'Close',
  'pty.teclas': 'Keys',
  'pty.colar': 'Paste',
  'pty.aba_aria': 'Interactive terminal sessions',
  'pty.sem_conta': 'Default account',
  'perm.sempre': "Approve and don't ask again",
  'perm.interromper': 'stop the agent',
  'perm.interromper_dica': 'Denies it and stops the agent now, instead of only denying this action.',
  'msg.na_fila': 'queued',
  'msg.entregue': 'delivered',
  'msg.nao_entregue': 'not delivered: {motivo}',
  'ponte.codex_fila': 'Codex has no message queue in this version (the codex CLI has no queue subcommand), so the box stays off.',
  'balao.concluido': 'Done',
  // timelapse completo (7.2): velocidades, picos, replay e volta ao vivo (timelapse.ts)
  'tl.voltar': 'Back to live',
  'tl.replay': 'REPLAY',
  'tl.picos': 'Peaks of the day',
  'tl.pico': 'Peak at {h}: {n} working',
  'tl.resumo': '{p} on campus · {n} working',
  'tl.legenda_presentes': 'present',
  'tl.legenda_trabalho': 'working',
  'mdia.demo_aviso': 'Made-up numbers: not real work, and nothing here is recorded.',
  'roda.tv': 'TV',
  'roda.videogame': 'Video game',
  'roda.fliperama': 'Arcade',
  'roda.pingpong': 'Ping-pong',
  'roda.papo': 'Kitchen chat',
  'roda.corredor': 'Hallway chat',
  'roda.jokenpo': 'Rock-paper-scissors',
  'roda.espelho': 'Bathroom mirror',
  'roda.prog.futebol': 'Soccer',
  'roda.prog.novela': 'Soap opera',
  'roda.prog.desenho': 'Cartoon',
  'roda.gol': '⚽ GOAL!',
  'roda.fofoca.0': '🤫 Seen {nome}?',
  'roda.fofoca.1': '🤭 {nome} vanished…',
  'roda.fofoca.2': '👀 Heard from {nome}…',
  'roda.fofoca.3': '😏 What about {nome}?',
  'traco.falante': 'Talker',
  'traco.competitivo': 'Competitive',
  'traco.caseiro': 'Homebody',
  'traco.gamer': 'Gamer',
  'traco.cinefilo': 'Movie buff',
  'traco.fofoqueiro': 'Gossip',
  'traco.atleta': 'Athlete',
  'traco.vaidoso': 'Vain',
  'traco.cafeinado': 'Caffeinated',
  'traco.brincalhao': 'Joker',
  'traco.sossegado': 'Easygoing',
  'traco.retro': 'Retro',
  'traco.sociavel': 'Sociable',
  'traco.curioso': 'Curious',
  'roda.traco': 'Personality',
};

const ES: Record<Chave, string> = {
  'topo.ajustes': 'Ajustes',
  'topo.ponte': 'Puente',
  'ponte.titulo': 'Puente con las CLIs',
  'ponte.explica': 'Aprobar pedidos y enviar mensajes desde la oficina. Funciona con los plugins agzos-* activos en la CLI. Apagado, las sesiones no cambian.',
  'ponte.aprovar': 'Aprobar pedidos',
  'ponte.mensagens': 'Enviar mensajes',
  'ponte.totp': 'Para aprobar desde la oficina, activa el TOTP en el login (--init-secret --totp).',
  'ponte.erro': 'No se pudo guardar: {erro}',
  'ponte.sessoes': '{n} sesiones con el buzón activo',
  'det.sem_caixa': 'Esta sesión no tiene el plugin agzos-mensagens activo.',
  'det.desligado': 'Mensajes apagados para esta CLI (ver Puente).',
  'login.sub': 'la oficina de tus agentes de código',
  'login.senha': 'Contraseña',
  'login.totp': 'Código del autenticador',
  'login.totp_opcional': '(si hay)',
  'login.usuario': 'Usuario',
  'login.usuario_dica': '(en blanco si es el principal)',
  'login.entrar': 'Entrar',
  'login.totp_pedido': 'contraseña correcta — ahora el código del autenticador',
  'erro.nao_deu': 'no funcionó',

  'topo.ao_vivo': 'en vivo',
  'topo.reconectando': 'reconectando…',
  'topo.meu_dia': 'Mi día',
  'topo.sair': 'Salir',
  'topo.idioma': 'Idioma',
  'num.salas': 'salas',
  'num.agentes': 'agentes',
  'num.trabalhando': 'trabajando',
  'num.subagentes': 'subagentes',
  'num.precisam': 'necesitan tu ayuda',
  'conta.sessoes.one': '{n} sesión',
  'conta.sessoes.other': '{n} sesiones',
  'conta.erro_leitura': 'error de lectura',

  'lateral.titulo': 'Oficina',
  'lateral.busca': 'Buscar agente, proyecto o CLI',
  'lateral.vazio': 'No hay agentes por aquí.',
  'projeto.trabalhando': '{n} de {total} trabajando',
  'agente.principal': 'principal',
  'agente.sub': 'sub',
  'subs.one': '{n} subagente',
  'subs.other': '{n} subagentes',
  'subs.trabalhando': '{n} trabajando',

  'status.working': 'trabajando',
  'status.thinking': 'pensando',
  'status.waiting': 'necesita tu ayuda',
  'status.shell': 'esperando la terminal',
  'status.idle': 'inactivo',

  'feed.titulo': 'Actividad',
  'feed.vazio': 'Todavía no pasó nada.',
  'feed.alternar': 'Mostrar u ocultar la actividad',

  'aviso.msg': '{nome} {texto} en {projeto}',
  'aviso.precisa': 'necesita tu ayuda',
  'acao.fechar': 'Cerrar',
  'balao.esperando': 'Esperándote',
  'cartao.subagente': 'subagente',
  'campus.terreo': 'Planta baja',
  'campus.andar': 'Piso {n}',
  'campus.andar_sigla': 'PB',
  'campus.sede': 'Sede {nome}',
  'campus.predio': 'Edificio {nome}',
  'campus.coworking': 'Coworking',
  'area.recepcao': 'Recepción',
  'area.copa': 'Cocina',
  'area.lounge': 'Lounge',
  'area.banheiros': 'Baños',
  'area.reuniao': 'Sala de reuniones',
  'area.servidores': 'Servidores',
  'area.terraco': 'Terraza',

  'det.agente': 'Agente',
  'det.subagente_de': 'subagente de {nome}',
  'det.sala': 'Sala',
  'det.ha': 'hace {tempo}',
  'det.mandar': 'Enviar mensaje',
  'det.placeholder': 'Mensaje para {nome}…',
  'det.enviar': 'Enviar',
  'det.tokens': '{n} tokens en esta sesión',
  'det.subagentes': 'Subagentes',
  'det.linha': 'Línea de tiempo',
  'det.sem_atividade': 'Sin actividad reciente.',
  'conversa.abrir': 'Ver conversación',
  'conversa.titulo': 'Conversación',
  'conversa.voltar': 'Volver',
  'conversa.aviso': 'solo lectura · secretos enmascarados',
  'conversa.carregando': 'Leyendo la conversación…',
  'conversa.vazia': 'Todavía no hay mensajes en esta sesión.',
  'vivo.aguardando': 'Esperando el próximo prompt',
  'vivo.assumir': 'Tomar desde aquí',
  'vivo.buscar': 'Buscar en la conversación',
  'vivo.copiar': 'Copiar',
  'vivo.encerrar': 'Cerrar',
  'vivo.encerrar_confirma': '¿Cerrar la terminal de la oficina de {nome}?',
  'vivo.esperando': 'Esperándote',
  'vivo.maximizar': 'Maximizar',
  'vivo.minimizar': 'Minimizar',
  'vivo.nada_achado': 'No se encontró nada.',
  'vivo.placeholder': 'Mensaje para {nome} (Enter envía, Shift+Enter nueva línea)',
  'vivo.principal': 'Agente principal',
  'vivo.resumo': 'Esta CLI no deja leer la conversación: mostrando las acciones que vio la oficina.',
  'vivo.saiu': 'La sesión salió de la oficina.',
  'vivo.sala': 'sala {nome}',
  'vivo.sem_ferramentas': 'Sin herramientas',
  'vivo.so_prompts': 'Solo prompts',
  'vivo.subagente': 'Subagente',
  'vivo.titulo': 'terminal',
  'vivo.trabalhando': 'trabajando',
  'vivo.tudo': 'Todo',
  'vivo.abrir': 'Abrir terminal',
  'conversa.voce': 'Tú',
  'conversa.agente': 'Agente',
  'conversa.ferramenta': 'Herramienta',
  // terminal (B23/B25)
  'topo.terminal': 'Terminal',
  'det.assumir': 'Asumir en la terminal',
  'det.assumir_dica': 'Abre Claude Code retomando esta sesión en una ventana de tmux. No los uses a la vez.',
  'terminal.titulo': 'Terminal',
  'terminal.explica': 'Sesiones tmux abiertas desde la oficina (agzos-*). La pantalla se actualiza cada segundo; los secretos salen enmascarados.',
  'terminal.novo': 'Nuevo agente',
  'terminal.cli': 'CLI',
  'terminal.pasta': 'Carpeta',
  'terminal.pasta_dica': 'ruta absoluta, dentro de /var/www o /root',
  'terminal.pedido': 'Primer pedido (opcional)',
  'terminal.criar': 'Abrir',
  'terminal.sessoes': 'Sesiones',
  'terminal.lista_aria': 'Sesiones de la terminal',
  'terminal.vazio': 'Ninguna sesión abierta desde la oficina.',
  'terminal.escolha': 'Elige una sesión de la lista.',
  'terminal.carregando': 'Leyendo la pantalla…',
  'terminal.tela_vazia': 'Pantalla vacía.',
  'terminal.tela_aria': 'Pantalla de la sesión',
  'terminal.digite': 'Escribe aquí y pulsa Enviar',
  'terminal.enter': 'Enter',
  'terminal.ctrl_c': 'Ctrl-C',
  'terminal.encerrar': 'Terminar',
  'terminal.encerrar_confirma': '¿Terminar esta sesión? El proceso se detiene al instante.',
  'terminal.aberto': 'Sesión abierta.',
  'terminal.encerrada': 'Sesión terminada.',
  'terminal.erro': 'No funcionó: {erro}',
  'det.aprovar': 'Aprobar',
  'det.recusar': 'Rechazar',
  'perg.titulo': 'Pregunta del agente',
  'perg.responder': 'Responder',
  'perg.recusar': 'Rechazar…',
  'perg.recusar_enviar': 'Rechazar',
  'perg.motivo': 'Motivo (opcional)',
  'perg.terminal': 'Responder en la terminal',
  'perg.terminal_dica': 'Cierra esta tarjeta sin decidir. La pregunta sigue en la terminal.',
  'perg.outro': 'Otro',
  'perg.outro_ph': 'Escribe tu respuesta',
  'perg.unica': 'Elige una opción',
  'perg.multipla': 'Puedes elegir más de una',
  'det.agente_principal': 'Agente principal',
  'det.sessao': 'Sesión',
  'det.sem_titulo': 'Sesión sin título todavía',
  'det.sala_centraliza': 'Centrar la sala {nome} en el campus',
  'det.seguir': 'Seguir',
  'det.seguir_titulo': 'La cámara sigue a este agente',
  'det.centralizar': 'Centrar',
  'det.centralizar_titulo': 'Centra la cámara en este agente, una vez',
  'det.terminal_vivo': 'Abrir terminal · en vivo',
  'det.terminal_vivo_titulo': 'Abre la terminal de la oficina de esta sesión (tecla T)',
  'det.terminal_sem_sessao': 'Esta sesión no está en una terminal de la oficina. Usa «Asumir en la terminal» para abrir una.',
  'det.agora': 'Ahora',
  'det.concluiu': '✅ Terminó en {duracao}',
  'det.sem_turno': 'Ningún turno terminado todavía.',
  'det.mandar_dica': 'Enter envía · Shift+Enter agrega una línea',
  'det.nenhum_subagente': 'Ningún subagente disparado todavía',
  'det.disparados': 'Disparados en total: {n}',
  'det.comando': 'Comando',
  'social.titulo': 'Vida social',
  'social.vazio': 'La vida social de este personaje todavía no empezó.',
  'social.moedas': 'monedas',
  'social.ganhos': '+{n} ganados trabajando',
  'social.vitoria.one': '{n} victoria',
  'social.vitoria.other': '{n} victorias',
  'social.derrota.one': '{n} derrota',
  'social.derrota.other': '{n} derrotas',
  'social.bordao': 'Muletilla: “{texto}”',
  'social.hobbies': 'Pasatiempos',
  'social.amizades': 'Amistades',
  'social.sem_amigos': 'Ninguna amistad todavía.',
  'social.rivalidades': 'Rivalidades',
  'social.sem_rivais': 'Ninguna rivalidad todavía.',
  'social.extrato': 'Extracto',
  'social.extrato_vazio': 'Sin movimientos todavía.',
  'social.livre': 'Nada por ahora',
  'social.fazendo': 'Haciendo ahora',
  'tempo.seg': '{n}s',
  'tempo.min_seg': '{m}min {s}s',
  'tempo.h': '{n} h',
  'tempo.d': '{n} d',
  'tempo.agora': 'ahora',
  'tempo.min': '{n} min',
  'tempo.h_min': '{h} h {m} min',

  'dia.agentes.one': 'agente',
  'dia.agentes.other': 'agentes',
  'dia.em': 'en',
  'dia.projetos.one': 'proyecto',
  'dia.projetos.other': 'proyectos',
  'dia.por_cli': 'Por CLI',
  'dia.tokens': '<b>{n}</b> tokens en las sesiones abiertas',
  'dia.atividades.one': '{n} actividad registrada',
  'dia.atividades.other': '{n} actividades registradas',
  'dia.anterior': 'Día anterior',
  'dia.proximo': 'Día siguiente',
  'dia.carregando': 'cargando…',
  'dia.erro': 'no se pudo leer este día',
  'dia.vazio': 'Nada registrado en este día.',
  'dia.barra': 'Tiempo por estado',
  'dia.resumo': '<b>{trabalho}</b> trabajando · <b>{espera}</b> esperando tu ayuda',
  'dia.projetos': 'Proyectos que más trabajaron',
  'dia.aprovacoes.one': '{n} pedido de aprobación',
  'dia.aprovacoes.other': '{n} pedidos de aprobación',
  'dia.tokens_do_dia': '<b>{n}</b> tokens gastados en el día',
  'dia.timelapse': 'Timelapse',
  'tl.titulo': 'Timelapse del día',
  'tl.fechar': 'Cerrar timelapse',
  'tl.tocar': 'Reproducir',
  'tl.pausar': 'Pausar',
  'tl.barra': 'Línea de tiempo del día',
  'tl.velocidade': 'Velocidad',
  'tl.carregando': 'cargando el timelapse…',
  'tl.erro': 'no se pudo cargar el timelapse',
  'tl.vazio': 'nada grabado en este día',
  // barra de ferramentas (toolbar.ts)
  'barra.ferramentas': 'Herramientas de la oficina',
  'barra.grupo_vista': 'Vista',
  'barra.grupo_paineis': 'Paneles',
  'barra.visao': 'Vista general',
  'barra.afastar': 'Alejar',
  'barra.aproximar': 'Acercar',
  'barra.entregas': 'Entregas',
  'barra.historico': 'Historial de sesiones',
  'barra.tema': 'Tema de la pantalla',
  'barra.ajuda': 'Ayuda y atajos',
  'mdia.dia': 'Día',
  'mdia.dia_aria': 'Elegir el día',
  'mdia.hoje': 'Hoy',
  'mdia.ontem': 'Ayer',
  'mdia.atualizado': 'Actualizado a las {hora} · se actualiza cada 30 s',
  'mdia.esperando': 'Esperando por ti',
  'mdia.esperas.one': '{n} espera',
  'mdia.esperas.other': '{n} esperas',
  'mdia.maior': 'Espera más larga: {duracao} · {nome} en {projeto}, a las {hora}',
  'mdia.trabalhando': 'Trabajando',
  'mdia.trabalhando_sub': 'suma del tiempo de todos los agentes',
  'mdia.sessoes': 'Sesiones',
  'mdia.subagentes.one': '{n} subagente',
  'mdia.subagentes.other': '{n} subagentes',
  'mdia.pedidos': 'Pedidos',
  'mdia.pedido.one': 'pedido',
  'mdia.pedido.other': 'pedidos',
  'mdia.ferramenta.one': 'herramienta',
  'mdia.ferramenta.other': 'herramientas',
  'mdia.concluida.one': 'tarea terminada',
  'mdia.concluida.other': 'tareas terminadas',
  'mdia.tokens': 'Tokens',
  'mdia.tokens_sub': 'la CLI no separa entrada y salida',
  'mdia.custo': 'Costo',
  'mdia.custo_sem': 'Los transcripts no trajeron el costo',
  'mdia.legenda': 'Leyenda',
  'mdia.leg.esperando': 'Esperando por ti',
  'mdia.leg.trabalhando': 'Trabajando',
  'mdia.leg.ocioso': 'Ocioso',
  'mdia.leg.shell': 'Esperando el shell',
  'mdia.por_hora': 'Por hora',
  'mdia.ver_tabela': 'Ver tabla',
  'mdia.ver_grafico': 'Ver gráfico',
  'mdia.horas_aria': 'Tiempo de los agentes por hora y estado',
  'mdia.tabela_horas': 'Tiempo de los agentes por hora y estado',
  'mdia.col_hora': 'Hora',
  'mdia.por_projeto': 'Por proyecto',
  'mdia.proj_linha': '{espera} esperando por ti · {total} en total',
  'mdia.tabela_projetos': 'Tiempo de los agentes por proyecto y estado',
  'mdia.col_projeto': 'Proyecto',
  'mdia.col_total': 'Total',
  'mdia.maiores_esperas': 'Esperas más largas',
  'mdia.nenhuma_espera': 'Ninguna espera este día.',
  'mdia.agora': 'ahora',
  'mdia.motivo.permissao': 'aprobar un permiso',
  'mdia.motivo.pergunta': 'responder una pregunta',
  'mdia.motivo.escolha': 'elegir una opción',
  'mdia.por_conta': 'Por cuenta',
  'mdia.sessao.one': 'sesión',
  'mdia.sessao.other': 'sesiones',
  'mdia.sub': 'sub',
  'mdia.conta_fatia': '{p}% del trabajo',
  'mdia.rodape_1': 'El tiempo de agentes es la suma: dos agentes trabajando 1 h dan 2 h.',
  'mdia.rodape_2': '“Esperando por ti” es la mano levantada: permiso, pregunta o elección que te espera.',
  'mdia.rodape_3': 'Solo cuenta mientras el escritorio está en marcha.',
  'mdia.timelapse': 'Ver timelapse del día',
  // histórico de sessões (T4)
  'vivo.encerrada': 'Sesión terminada a las {hora}',
  'historico.titulo': 'Historial de sesiones',
  'historico.botao': 'Historial',
  'historico.explica': 'Últimos 7 días, de todas las cuentas. Haz clic para leer la conversación en la terminal.',
  'historico.busca': 'Buscar por título, proyecto o cuenta',
  'historico.hoje': 'Hoy',
  'historico.ontem': 'Ayer',
  'historico.sem_titulo': 'sin título',
  'historico.aberta': 'abierta',
  'historico.sessoes.one': '{n} sesión',
  'historico.sessoes.other': '{n} sesiones',
  'historico.vazio': 'Ninguna sesión en los últimos 7 días.',
  'historico.nada': 'Nada encontrado para esta búsqueda.',
  'historico.carregando': 'cargando el historial…',
  // entregas (janela: lo que terminó cada agente)
  'entregas.botao': 'Entregas',
  'entregas.titulo': 'Entregas',
  'entregas.explica': 'Lo que terminó cada agente, en pocas palabras. Toca para ver el pedido, la respuesta y los adjuntos.',
  'entregas.vazio': 'Todavía no hay entregas. Cuando un agente termine un pedido, aparecerá aquí.',
  'entregas.passos.one': '{n} paso',
  'entregas.passos.other': '{n} pasos',
  'entregas.pedido': 'Pedido',
  'entregas.resposta': 'Respuesta',
  'entregas.anexos': 'Adjuntos',
  'entregas.anexo': 'Adjunto {n}',
  'entregas.voltar': 'Volver',
  'entregas.abrir_imagem': 'Abrir la imagen en otra pestaña',
  'entregas.sem_pedido': 'Pedido no registrado.',
  'entregas.sumiu': 'Esta entrega ya no está en el registro.',
  'entregas.erro': 'No se pudieron cargar las entregas.',
  // PWA (pwa.ts): instalar, nueva versión y sin conexión
  'pwa.instalar': 'Instalar',
  'pwa.instalar_texto': 'Instala la oficina en la pantalla de inicio: se abre a pantalla completa, como una app.',
  'pwa.nova_versao': 'Nueva versión disponible',
  'pwa.atualizar': 'Actualizar',
  'pwa.sem_conexao': 'Sin conexión con la oficina. Los datos vuelven cuando el servidor responda.',
  'pwa.tentar': 'Intentar de nuevo',
  'pwa.ios': 'Para instalar: toca Compartir y luego “Añadir a la pantalla de inicio”.',
  'pwa.agora_nao': 'Ahora no',
  // móvil (mobile.ts): HUD, pestañas y bottom sheets
  'jogo.abas': 'Pestañas del juego',
  'jogo.aba.escritorio': 'Oficina',
  'jogo.aba.agentes': 'Agentes',
  'jogo.aba.entregas': 'Entregas',
  'jogo.aba.dia': 'Mi día',
  'jogo.aba.mais': 'Más',
  'jogo.ultima': 'última actividad',
  'jogo.sem_atividade': 'nada todavía',
  'jogo.consumo': 'Consumo del plan',
  'jogo.consumo_vazio': 'Ninguna CLI informó su consumo todavía.',
  'jogo.arrastar': 'Arrastra para cambiar la altura o cerrar',
  'vivo.voltar': 'Volver',
  'vivo.menu': 'Más opciones',
  'vivo.filtros': 'Filtros',
  'missao.titulo': 'Te necesita',
  'missao.de': '{n} de {total}',
  'missao.so_admin': 'Solo un admin puede decidir. Aquí lo ves como aviso.',
  'missao.entendi': 'Entendido',
  // notificações no celular (B29-M5): painel do Mais e do CMS
  'push.item': 'Notificaciones',
  'push.ativar': 'Activar notificaciones en este dispositivo',
  'push.desativar': 'Desactivar en este dispositivo',
  'push.testar': 'Enviar prueba',
  'push.teste_ok': 'Prueba enviada. Debería llegar en unos instantes.',
  'push.teste_sem_aparelho': 'Activa las notificaciones en este dispositivo antes de la prueba.',
  'push.teste_falhou': 'La prueba no llegó a ningún dispositivo.',
  'push.ativo': 'Este dispositivo recibe notificaciones.',
  'push.inativo': 'Este dispositivo no recibe notificaciones.',
  'push.sem_suporte': 'Este navegador no puede recibir notificaciones de la oficina.',
  'push.ios': 'En iPhone y iPad, las notificaciones solo funcionan con la app instalada en la pantalla de inicio (iOS 16.4 o posterior). Abre el botón Compartir, “Añadir a la pantalla de inicio”, y actívalas desde ahí.',
  'push.negada': 'Se denegó el permiso de notificaciones. Permítelo en la configuración del navegador.',
  'push.falhou': 'No se pudo completar. Inténtalo de nuevo.',
  'push.quando': 'Avisar cuando',
  'push.precisa': 'Alguien te necesita',
  'push.concluiu': 'Un agente termina',
  'push.aparelhos': 'Dispositivos suscritos',
  'push.nenhum': 'Ningún dispositivo suscrito.',
  'push.remover': 'quitar',
  'push.este': 'este dispositivo',
  'push.salvo': 'Guardado.',
  // topo: contador de shells (5.1)
  'num.shells': 'shells en marcha',
  'num.shells_dica': 'Clic para ir al próximo agente con un shell en marcha',
  'sons.titulo': 'Sonidos',
  'sons.dica': 'Apagados por defecto. Sonidos suaves, hechos en el navegador. Con la pestaña oculta, solo suena la campana.',
  'sons.volume': 'Volumen',
  'sons.sino': 'Campana: alguien necesita tu ayuda',
  'sons.teclado': 'Teclado: quien trabaja en las salas visibles',
  'sons.elevador': 'Ascensor: alguien llega o se va',
  'sons.concluida': 'Pop: tarea terminada',
  'sons.rodas': 'Juegos: ping-pong, arcade, piedra, papel o tijera',
  'sons.fechar': 'Cerrar',
  // demonstração pela interface (demo.ts)
  'demo.selo': 'demostración',
  'demo.selo_titulo': 'Agentes ficticios para llenar la oficina. No cuentan en Mi día.',
  'demo.ver': 'Ver demostración',
  'demo.faixa': 'agentes ficticios junto a los reales',
  'demo.desligar': 'Apagar',
  'demo.erro': 'No se pudo cambiar la demostración: {erro}',
  'demo.sem_resposta': 'sin respuesta del servidor',
  'demo.painel': 'Agente ficticio: no es una sesión real.',
  // editor de personaje (ed.*) y el lápiz del panel
  'det.editar_personagem': 'Editar personaje',
  'ed.titulo_projeto': 'Personaje del agente principal de {projeto}',
  'ed.titulo_sessao': 'Personaje de {nome} ({projeto})',
  'ed.fechar': 'Cerrar',
  'ed.previa': 'Vista previa del personaje',
  'ed.sortear': 'Sortear',
  'ed.voltar_sorteio': 'Volver al sorteo',
  'ed.salvar': 'Guardar',
  'ed.salvando': 'Guardando…',
  'ed.cancelar': 'Cancelar',
  'ed.salvo': 'Personaje guardado.',
  'ed.erro': 'No se pudo guardar: {erro}',
  'ed.nome': 'Nombre',
  'ed.nome_dica': 'De 1 a {max} letras, sin repetir el nombre de alguien de la oficina, de otro personaje o de otro proyecto.',
  'ed.nome_vazio': 'Escribe un nombre.',
  'ed.nome_longo': 'Usa hasta {max} letras.',
  'ed.nome_repetido': 'Ese nombre ya está en uso.',
  'ed.grupo_corpo': 'Cuerpo',
  'ed.grupo_pele': 'Piel',
  'ed.grupo_cabelo': 'Pelo',
  'ed.grupo_cabelo_cor': 'Color del pelo',
  'ed.grupo_barba': 'Barba',
  'ed.grupo_olhos': 'Ojos',
  'ed.grupo_acessorio': 'Accesorio',
  'ed.grupo_acessorio_cor': 'Color del accesorio',
  'ed.grupo_cima': 'Parte de arriba',
  'ed.grupo_cima_cor': 'Color de la parte de arriba',
  'ed.grupo_baixo': 'Parte de abajo',
  'ed.grupo_sapatos': 'Zapatos',
  'ed.nenhum': 'Ninguno',
  'ed.cor': 'Color {n}',
  'ed.peca_dica': 'La oficina dibuja un accesorio por personaje: gafas, barba y accesorio se reemplazan entre sí.',
  'ed.editar_pecas': 'Editar piezas',
  'det.estatisticas': 'Estadísticas',
  'stats.ferramentas': 'Herramientas',
  'stats.chamadas.one': '{n} llamada',
  'stats.chamadas.other': '{n} llamadas',
  'stats.entrada': 'Entrada',
  'stats.saida': 'Salida',
  'stats.custo': 'Costo',
  'stats.custo_sem': 'La CLI no grabó el costo de esta sesión',
  'stats.custo_sub': 'suma de las sesiones que informan el costo',
  'stats.linhas': 'Líneas modificadas',
  'stats.modelo': 'Modelo',
  'stats.branch': 'Rama',
  'stats.entrada_saida': 'entrada {e} · salida {s}',
  'stats.desatualizado': 'desactualizado',
  'stats.desatualizado_titulo': 'La lectura del consumo tiene más de 30 min y puede estar vieja',
  'stats.sem_cota': 'sin cuota',
  'stats.sem_cota_titulo': 'Codex informa crédito cero: sin cuota ahora',
  'mdia.leg_alterna': 'Haz clic para mostrar u ocultar este estado en los gráficos',
  'gh.t.pr-open': '🎉 abrió el PR{n}',
  'gh.t.pr-merge': '🎉 fusionó el PR{n}',
  'gh.t.release': '🎉 publicó la release{tag}',
  'gh.t.ci-fail': '🚨 CI falló{ramo}',
  'gh.t.ci-pass': '✅ CI en verde{ramo}',
  'gh.t.push': '⬆️ hizo push{ramo}',
  'gh.b.pr-open': '¡PR{n} abierto!',
  'gh.b.pr-merge': '¡PR{n} fusionado!',
  'gh.b.release': '¡Release{tag} publicada!',
  'gh.b.ci-pass': '¡CI en verde!',
  'gh.alarme': '🚨 CI falló',
  'pty.titulo': 'Terminal interactivo',
  'pty.nova': 'Nueva sesión',
  'pty.cli': 'CLI',
  'pty.conta': 'Cuenta',
  'pty.pasta': 'Carpeta del proyecto',
  'pty.pedido': 'Primer pedido (opcional)',
  'pty.criar': 'Abrir',
  'pty.encerrar': 'Cerrar sesión',
  'pty.confirmar': 'Haz clic otra vez para cerrar',
  'pty.assumir_confirma': 'Esto CIERRA la sesión abierta en la otra terminal y continúa la misma conversación aquí. ¿Continuar?',
  'pty.vazio': 'Ninguna sesión abierta. Abre una en “Nueva sesión”.',
  'pty.conectando': 'Conectando…',
  'pty.terminou': '[el proceso terminó]',
  'pty.sem_pty': 'El terminal interactivo necesita node-pty, que no está instalado en esta máquina. El terminal por tmux sigue funcionando.',
  'pty.fechar_aba': 'Cerrar',
  'pty.teclas': 'Teclas',
  'pty.colar': 'Pegar',
  'pty.aba_aria': 'Sesiones del terminal interactivo',
  'pty.sem_conta': 'Cuenta predeterminada',
  'perm.sempre': 'Aprobar y no volver a preguntar',
  'perm.interromper': 'interrumpir al agente',
  'perm.interromper_dica': 'Rechaza y detiene al agente ahora, en vez de solo rechazar esta acción.',
  'msg.na_fila': 'en cola',
  'msg.entregue': 'entregado',
  'msg.nao_entregue': 'no entregado: {motivo}',
  'ponte.codex_fila': 'Codex todavía no tiene cola de mensajes en esta versión (no existe el subcomando queue), así que la caja sigue apagada.',
  'balao.concluido': 'Terminado',
  // timelapse completo (7.2): velocidades, picos, replay e volta ao vivo (timelapse.ts)
  'tl.voltar': 'Volver al vivo',
  'tl.replay': 'REPLAY',
  'tl.picos': 'Picos del día',
  'tl.pico': 'Pico a las {h}: {n} trabajando',
  'tl.resumo': '{p} en el campus · {n} trabajando',
  'tl.legenda_presentes': 'presentes',
  'tl.legenda_trabalho': 'trabajando',
  'mdia.demo_aviso': 'Números ficticios: no son trabajo real y nada de esto se guarda.',
  'roda.tv': 'TV',
  'roda.videogame': 'Videojuego',
  'roda.fliperama': 'Arcade',
  'roda.pingpong': 'Ping-pong',
  'roda.papo': 'Charla en la cocina',
  'roda.corredor': 'Charla de pie',
  'roda.jokenpo': 'Piedra, papel o tijera',
  'roda.espelho': 'Espejo del baño',
  'roda.prog.futebol': 'Fútbol',
  'roda.prog.novela': 'Telenovela',
  'roda.prog.desenho': 'Dibujos',
  'roda.gol': '⚽ ¡GOL!',
  'roda.fofoca.0': '🤫 ¿Viste a {nome}?',
  'roda.fofoca.1': '🤭 {nome} desapareció…',
  'roda.fofoca.2': '👀 Oí de {nome}…',
  'roda.fofoca.3': '😏 ¿Y {nome}, eh?',
  'traco.falante': 'Hablador',
  'traco.competitivo': 'Competitivo',
  'traco.caseiro': 'Casero',
  'traco.gamer': 'Gamer',
  'traco.cinefilo': 'Cinéfilo',
  'traco.fofoqueiro': 'Chismoso',
  'traco.atleta': 'Atleta',
  'traco.vaidoso': 'Vanidoso',
  'traco.cafeinado': 'Cafeinado',
  'traco.brincalhao': 'Bromista',
  'traco.sossegado': 'Tranquilo',
  'traco.retro': 'Retro',
  'traco.sociavel': 'Sociable',
  'traco.curioso': 'Curioso',
  'roda.traco': 'Personalidad',
};

// Em francês, o espaço antes de dois-pontos é sem quebra ( ), como manda a tipografia
const FR: Record<Chave, string> = {
  'topo.ajustes': 'Réglages',
  'topo.ponte': 'Pont',
  'ponte.titulo': 'Pont avec les CLI',
  'ponte.explica': 'Approuver les demandes et envoyer des messages depuis le bureau. Fonctionne avec les plugins agzos-* activés dans la CLI. Désactivé, rien ne change dans les sessions.',
  'ponte.aprovar': 'Approuver les demandes',
  'ponte.mensagens': 'Envoyer des messages',
  'ponte.totp': 'Pour approuver depuis le bureau, activez le TOTP à la connexion (--init-secret --totp).',
  'ponte.erro': 'Impossible d’enregistrer : {erro}',
  'ponte.sessoes': '{n} sessions avec la boîte de messages active',
  'det.sem_caixa': 'Cette session n’a pas le plugin agzos-mensagens activé.',
  'det.desligado': 'Messages désactivés pour cette CLI (voir Pont).',
  'login.sub': 'le bureau de vos agents de code',
  'login.senha': 'Mot de passe',
  'login.totp': 'Code de l’authentificateur',
  'login.totp_opcional': '(si besoin)',
  'login.usuario': 'Utilisateur',
  'login.usuario_dica': '(vide pour l’administrateur principal)',
  'login.entrar': 'Se connecter',
  'login.totp_pedido': 'mot de passe ok — maintenant le code de l’authentificateur',
  'erro.nao_deu': 'échec',

  'topo.ao_vivo': 'en direct',
  'topo.reconectando': 'reconnexion…',
  'topo.meu_dia': 'Ma journée',
  'topo.sair': 'Quitter',
  'topo.idioma': 'Langue',
  'num.salas': 'salles',
  'num.agentes': 'agents',
  'num.trabalhando': 'au travail',
  'num.subagentes': 'sous-agents',
  'num.precisam': 'ont besoin de vous',
  'conta.sessoes.one': '{n} session',
  'conta.sessoes.other': '{n} sessions',
  'conta.erro_leitura': 'erreur de lecture',

  'lateral.titulo': 'Bureau',
  'lateral.busca': 'Chercher un agent, un projet ou une CLI',
  'lateral.vazio': 'Aucun agent ici.',
  'projeto.trabalhando': '{n} sur {total} au travail',
  'agente.principal': 'principal',
  'agente.sub': 'sub',
  'subs.one': '{n} sous-agent',
  'subs.other': '{n} sous-agents',
  'subs.trabalhando': '{n} au travail',

  'status.working': 'au travail',
  'status.thinking': 'réfléchit',
  'status.waiting': 'a besoin de vous',
  'status.shell': 'attend le shell',
  'status.idle': 'inactif',

  'feed.titulo': 'Activité',
  'feed.vazio': 'Rien ne s’est encore passé.',
  'feed.alternar': 'Afficher ou masquer l’activité',

  'aviso.msg': '{nome} {texto} dans {projeto}',
  'aviso.precisa': 'a besoin de vous',
  'acao.fechar': 'Fermer',
  'balao.esperando': 'Vous attend',
  'cartao.subagente': 'sous-agent',
  'campus.terreo': 'Rez-de-chaussée',
  'campus.andar': 'Étage {n}',
  'campus.andar_sigla': 'RDC',
  'campus.sede': 'Siège {nome}',
  'campus.predio': 'Bâtiment {nome}',
  'campus.coworking': 'Coworking',
  'area.recepcao': 'Accueil',
  'area.copa': 'Kitchenette',
  'area.lounge': 'Lounge',
  'area.banheiros': 'Toilettes',
  'area.reuniao': 'Salle de réunion',
  'area.servidores': 'Serveurs',
  'area.terraco': 'Terrasse',

  'det.agente': 'Agent',
  'det.subagente_de': 'sous-agent de {nome}',
  'det.sala': 'Salle',
  'det.ha': 'il y a {tempo}',
  'det.mandar': 'Envoyer un message',
  'det.placeholder': 'Message pour {nome}…',
  'det.enviar': 'Envoyer',
  'det.tokens': '{n} tokens dans cette session',
  'det.subagentes': 'Sous-agents',
  'det.linha': 'Chronologie',
  'det.sem_atividade': 'Aucune activité récente.',
  'conversa.abrir': 'Voir la conversation',
  'conversa.titulo': 'Conversation',
  'conversa.voltar': 'Retour',
  'conversa.aviso': 'lecture seule · secrets masqués',
  'conversa.carregando': 'Lecture de la conversation…',
  'conversa.vazia': 'Aucun message dans cette session pour l’instant.',
  'vivo.aguardando': 'En attente du prochain prompt',
  'vivo.assumir': 'Reprendre ici',
  'vivo.buscar': 'Chercher dans la conversation',
  'vivo.copiar': 'Copier',
  'vivo.encerrar': 'Arrêter',
  'vivo.encerrar_confirma': 'Arrêter le terminal du bureau de {nome} ?',
  'vivo.esperando': 'Vous attend',
  'vivo.maximizar': 'Agrandir',
  'vivo.minimizar': 'Réduire',
  'vivo.nada_achado': 'Rien trouvé.',
  'vivo.placeholder': 'Message à {nome} (Entrée envoie, Maj+Entrée nouvelle ligne)',
  'vivo.principal': 'Agent principal',
  'vivo.resumo': 'Cette CLI ne laisse pas lire la conversation : voici les actions vues par le bureau.',
  'vivo.saiu': 'La session a quitté le bureau.',
  'vivo.sala': 'salle {nome}',
  'vivo.sem_ferramentas': 'Sans outils',
  'vivo.so_prompts': 'Prompts seulement',
  'vivo.subagente': 'Sous-agent',
  'vivo.titulo': 'terminal',
  'vivo.trabalhando': 'au travail',
  'vivo.tudo': 'Tout',
  'vivo.abrir': 'Ouvrir le terminal',
  'conversa.voce': 'Vous',
  'conversa.agente': 'Agent',
  'conversa.ferramenta': 'Outil',
  // terminal (B23/B25)
  'topo.terminal': 'Terminal',
  'det.assumir': 'Reprendre dans le terminal',
  'det.assumir_dica': 'Ouvre Claude Code en reprenant cette session dans une fenêtre tmux. Ne les utilisez pas en même temps.',
  'terminal.titulo': 'Terminal',
  'terminal.explica': 'Sessions tmux ouvertes depuis le bureau (agzos-*). L’écran se rafraîchit chaque seconde ; les secrets sont masqués.',
  'terminal.novo': 'Nouvel agent',
  'terminal.cli': 'CLI',
  'terminal.pasta': 'Dossier',
  'terminal.pasta_dica': 'chemin absolu, dans /var/www ou /root',
  'terminal.pedido': 'Première demande (facultatif)',
  'terminal.criar': 'Ouvrir',
  'terminal.sessoes': 'Sessions',
  'terminal.lista_aria': 'Sessions du terminal',
  'terminal.vazio': 'Aucune session ouverte depuis le bureau.',
  'terminal.escolha': 'Choisissez une session dans la liste.',
  'terminal.carregando': 'Lecture de l’écran…',
  'terminal.tela_vazia': 'Écran vide.',
  'terminal.tela_aria': 'Écran de la session',
  'terminal.digite': 'Tapez ici puis Envoyer',
  'terminal.enter': 'Entrée',
  'terminal.ctrl_c': 'Ctrl-C',
  'terminal.encerrar': 'Arrêter',
  'terminal.encerrar_confirma': 'Arrêter cette session ? Le processus s’arrête tout de suite.',
  'terminal.aberto': 'Session ouverte.',
  'terminal.encerrada': 'Session arrêtée.',
  'terminal.erro': 'Échec : {erro}',
  'det.aprovar': 'Approuver',
  'det.recusar': 'Refuser',
  'perg.titulo': 'Question de l’agent',
  'perg.responder': 'Répondre',
  'perg.recusar': 'Refuser…',
  'perg.recusar_enviar': 'Refuser',
  'perg.motivo': 'Motif (facultatif)',
  'perg.terminal': 'Répondre dans le terminal',
  'perg.terminal_dica': 'Ferme cette carte sans répondre. La question reste dans le terminal.',
  'perg.outro': 'Autre',
  'perg.outro_ph': 'Écrivez votre réponse',
  'perg.unica': 'Choisissez une option',
  'perg.multipla': 'Vous pouvez en choisir plusieurs',
  'det.agente_principal': 'Agent principal',
  'det.sessao': 'Session',
  'det.sem_titulo': 'Session encore sans titre',
  'det.sala_centraliza': 'Centrer la salle {nome} sur le campus',
  'det.seguir': 'Suivre',
  'det.seguir_titulo': 'La caméra suit cet agent',
  'det.centralizar': 'Centrer',
  'det.centralizar_titulo': 'Centre la caméra sur cet agent, une fois',
  'det.terminal_vivo': 'Ouvrir le terminal · en direct',
  'det.terminal_vivo_titulo': 'Ouvre le terminal du bureau de cette session (touche T)',
  'det.terminal_sem_sessao': 'Cette session n’est pas dans un terminal du bureau. Utilisez « Reprendre dans le terminal » pour en ouvrir un.',
  'det.agora': 'Maintenant',
  'det.concluiu': '✅ Terminé en {duracao}',
  'det.sem_turno': 'Aucun tour terminé pour l’instant.',
  'det.mandar_dica': 'Entrée envoie · Maj+Entrée ajoute une ligne',
  'det.nenhum_subagente': 'Aucun sous-agent lancé pour l’instant',
  'det.disparados': 'Lancés au total : {n}',
  'det.comando': 'Commande',
  'social.titulo': 'Vie sociale',
  'social.vazio': 'La vie sociale de ce personnage n’a pas encore commencé.',
  'social.moedas': 'pièces',
  'social.ganhos': '+{n} gagnés en travaillant',
  'social.vitoria.one': '{n} victoire',
  'social.vitoria.other': '{n} victoires',
  'social.derrota.one': '{n} défaite',
  'social.derrota.other': '{n} défaites',
  'social.bordao': 'Expression : “{texto}”',
  'social.hobbies': 'Loisirs',
  'social.amizades': 'Amitiés',
  'social.sem_amigos': 'Aucune amitié pour l’instant.',
  'social.rivalidades': 'Rivalités',
  'social.sem_rivais': 'Aucune rivalité pour l’instant.',
  'social.extrato': 'Relevé',
  'social.extrato_vazio': 'Aucune écriture pour l’instant.',
  'social.livre': 'Rien pour le moment',
  'social.fazendo': 'En ce moment',
  'tempo.seg': '{n} s',
  'tempo.min_seg': '{m} min {s} s',
  'tempo.h': '{n} h',
  'tempo.d': '{n} j',
  'tempo.agora': 'maintenant',
  'tempo.min': '{n} min',
  'tempo.h_min': '{h} h {m} min',

  'dia.agentes.one': 'agent',
  'dia.agentes.other': 'agents',
  'dia.em': 'dans',
  'dia.projetos.one': 'projet',
  'dia.projetos.other': 'projets',
  'dia.por_cli': 'Par CLI',
  'dia.tokens': '<b>{n}</b> tokens dans les sessions ouvertes',
  'dia.atividades.one': '{n} activité enregistrée',
  'dia.atividades.other': '{n} activités enregistrées',
  'dia.anterior': 'Jour précédent',
  'dia.proximo': 'Jour suivant',
  'dia.carregando': 'chargement…',
  'dia.erro': 'impossible de lire ce jour',
  'dia.vazio': 'Rien d’enregistré ce jour-là.',
  'dia.barra': 'Temps par statut',
  'dia.resumo': '<b>{trabalho}</b> au travail · <b>{espera}</b> en attente de vous',
  'dia.projetos': 'Projets les plus actifs',
  'dia.aprovacoes.one': '{n} demande d’approbation',
  'dia.aprovacoes.other': '{n} demandes d’approbation',
  'dia.tokens_do_dia': '<b>{n}</b> tokens dépensés ce jour-là',
  'dia.timelapse': 'Timelapse',
  'tl.titulo': 'Timelapse de la journée',
  'tl.fechar': 'Fermer le timelapse',
  'tl.tocar': 'Lire',
  'tl.pausar': 'Pause',
  'tl.barra': 'Chronologie de la journée',
  'tl.velocidade': 'Vitesse',
  'tl.carregando': 'chargement du timelapse…',
  'tl.erro': 'impossible de charger le timelapse',
  'tl.vazio': 'rien enregistré ce jour-là',
  // barre d'outils (toolbar.ts)
  'barra.ferramentas': 'Outils du bureau',
  'barra.grupo_vista': 'Vue',
  'barra.grupo_paineis': 'Panneaux',
  'barra.visao': "Vue d'ensemble",
  'barra.afastar': 'Dézoomer',
  'barra.aproximar': 'Zoomer',
  'barra.entregas': 'Livraisons',
  'barra.historico': 'Historique des sessions',
  'barra.tema': "Thème de l'écran",
  'barra.ajuda': 'Aide et raccourcis',
  'mdia.dia': 'Jour',
  'mdia.dia_aria': 'Choisir le jour',
  'mdia.hoje': 'Aujourd’hui',
  'mdia.ontem': 'Hier',
  'mdia.atualizado': 'Mis à jour à {hora} · actualisé toutes les 30 s',
  'mdia.esperando': 'En attente de vous',
  'mdia.esperas.one': '{n} attente',
  'mdia.esperas.other': '{n} attentes',
  'mdia.maior': 'Attente la plus longue : {duracao} · {nome} dans {projeto}, à {hora}',
  'mdia.trabalhando': 'Au travail',
  'mdia.trabalhando_sub': 'somme du temps de tous les agents',
  'mdia.sessoes': 'Sessions',
  'mdia.subagentes.one': '{n} sous-agent',
  'mdia.subagentes.other': '{n} sous-agents',
  'mdia.pedidos': 'Demandes',
  'mdia.pedido.one': 'demande',
  'mdia.pedido.other': 'demandes',
  'mdia.ferramenta.one': 'outil',
  'mdia.ferramenta.other': 'outils',
  'mdia.concluida.one': 'tâche terminée',
  'mdia.concluida.other': 'tâches terminées',
  'mdia.tokens': 'Tokens',
  'mdia.tokens_sub': 'entrée et sortie non séparées par la CLI',
  'mdia.custo': 'Coût',
  'mdia.custo_sem': 'Les transcriptions n’ont pas fourni le coût',
  'mdia.legenda': 'Légende',
  'mdia.leg.esperando': 'En attente de vous',
  'mdia.leg.trabalhando': 'Au travail',
  'mdia.leg.ocioso': 'Inactif',
  'mdia.leg.shell': 'En attente du shell',
  'mdia.por_hora': 'Par heure',
  'mdia.ver_tabela': 'Voir le tableau',
  'mdia.ver_grafico': 'Voir le graphique',
  'mdia.horas_aria': 'Temps des agents par heure et état',
  'mdia.tabela_horas': 'Temps des agents par heure et état',
  'mdia.col_hora': 'Heure',
  'mdia.por_projeto': 'Par projet',
  'mdia.proj_linha': '{espera} en attente de vous · {total} au total',
  'mdia.tabela_projetos': 'Temps des agents par projet et état',
  'mdia.col_projeto': 'Projet',
  'mdia.col_total': 'Total',
  'mdia.maiores_esperas': 'Plus longues attentes',
  'mdia.nenhuma_espera': 'Aucune attente ce jour.',
  'mdia.agora': 'maintenant',
  'mdia.motivo.permissao': 'approuver une permission',
  'mdia.motivo.pergunta': 'répondre à une question',
  'mdia.motivo.escolha': 'choisir une option',
  'mdia.por_conta': 'Par compte',
  'mdia.sessao.one': 'session',
  'mdia.sessao.other': 'sessions',
  'mdia.sub': 'sous',
  'mdia.conta_fatia': '{p} % du travail',
  'mdia.rodape_1': 'Le temps des agents est la somme : deux agents qui travaillent 1 h font 2 h.',
  'mdia.rodape_2': '« En attente de vous » est la main levée : permission, question ou choix qui attend.',
  'mdia.rodape_3': 'Ne compte que pendant que le bureau tourne.',
  'mdia.timelapse': 'Voir le timelapse du jour',
  // histórico de sessões (T4)
  'vivo.encerrada': 'Session terminée à {hora}',
  'historico.titulo': 'Historique des sessions',
  'historico.botao': 'Historique',
  'historico.explica': 'Les 7 derniers jours, de tous les comptes. Cliquez pour lire la conversation dans le terminal.',
  'historico.busca': 'Rechercher par titre, projet ou compte',
  'historico.hoje': 'Aujourd’hui',
  'historico.ontem': 'Hier',
  'historico.sem_titulo': 'sans titre',
  'historico.aberta': 'ouverte',
  'historico.sessoes.one': '{n} session',
  'historico.sessoes.other': '{n} sessions',
  'historico.vazio': 'Aucune session ces 7 derniers jours.',
  'historico.nada': 'Rien trouvé pour cette recherche.',
  'historico.carregando': 'chargement de l’historique…',
  // entregas (fenêtre : ce que chaque agent a terminé)
  'entregas.botao': 'Livraisons',
  'entregas.titulo': 'Livraisons',
  'entregas.explica': 'Ce que chaque agent a terminé, en quelques mots. Touchez pour voir la demande, la réponse et les pièces jointes.',
  'entregas.vazio': 'Pas encore de livraison. Quand un agent termine une demande, elle apparaît ici.',
  'entregas.passos.one': '{n} étape',
  'entregas.passos.other': '{n} étapes',
  'entregas.pedido': 'Demande',
  'entregas.resposta': 'Réponse',
  'entregas.anexos': 'Pièces jointes',
  'entregas.anexo': 'Pièce jointe {n}',
  'entregas.voltar': 'Retour',
  'entregas.abrir_imagem': 'Ouvrir l’image dans un nouvel onglet',
  'entregas.sem_pedido': 'Demande non enregistrée.',
  'entregas.sumiu': 'Cette livraison n’est plus dans le registre.',
  'entregas.erro': 'Impossible de charger les livraisons.',
  // PWA (pwa.ts) : installer, nouvelle version et hors connexion
  'pwa.instalar': 'Installer',
  'pwa.instalar_texto': 'Installez le bureau sur l’écran d’accueil : il s’ouvre en plein écran, comme une appli.',
  'pwa.nova_versao': 'Nouvelle version disponible',
  'pwa.atualizar': 'Mettre à jour',
  'pwa.sem_conexao': 'Pas de connexion au bureau. Les données reviennent quand le serveur répond.',
  'pwa.tentar': 'Réessayer',
  'pwa.ios': 'Pour installer : touchez Partager, puis « Sur l’écran d’accueil ».',
  'pwa.agora_nao': 'Pas maintenant',
  // mobile (mobile.ts) : HUD, onglets et bottom sheets
  'jogo.abas': 'Onglets du jeu',
  'jogo.aba.escritorio': 'Bureau',
  'jogo.aba.agentes': 'Agents',
  'jogo.aba.entregas': 'Livraisons',
  'jogo.aba.dia': 'Ma journée',
  'jogo.aba.mais': 'Plus',
  'jogo.ultima': 'dernière activité',
  'jogo.sem_atividade': 'rien encore',
  'jogo.consumo': 'Consommation du forfait',
  'jogo.consumo_vazio': 'Aucune CLI n’a encore donné sa consommation.',
  'jogo.arrastar': 'Glisser pour changer la hauteur ou fermer',
  'vivo.voltar': 'Retour',
  'vivo.menu': 'Plus d’options',
  'vivo.filtros': 'Filtres',
  'missao.titulo': 'Besoin de vous',
  'missao.de': '{n} sur {total}',
  'missao.so_admin': 'Seul un admin peut décider. Vous le voyez à titre d’information.',
  'missao.entendi': 'Compris',
  // notificações no celular (B29-M5): painel do Mais e do CMS
  'push.item': 'Notifications',
  'push.ativar': 'Activer les notifications sur cet appareil',
  'push.desativar': 'Désactiver sur cet appareil',
  'push.testar': 'Envoyer un test',
  'push.teste_ok': 'Test envoyé. Il devrait arriver dans un instant.',
  'push.teste_sem_aparelho': 'Activez les notifications sur cet appareil avant le test.',
  'push.teste_falhou': 'Le test n’a atteint aucun appareil.',
  'push.ativo': 'Cet appareil reçoit les notifications.',
  'push.inativo': 'Cet appareil ne reçoit pas les notifications.',
  'push.sem_suporte': 'Ce navigateur ne peut pas recevoir les notifications du bureau.',
  'push.ios': 'Sur iPhone et iPad, les notifications ne fonctionnent qu’avec l’app installée sur l’écran d’accueil (iOS 16.4 ou plus récent). Ouvrez le bouton Partager, « Sur l’écran d’accueil », puis activez-les depuis l’app.',
  'push.negada': 'L’autorisation des notifications a été refusée. Autorisez-la dans les réglages du navigateur.',
  'push.falhou': 'Impossible de terminer. Réessayez.',
  'push.quando': 'Me prévenir quand',
  'push.precisa': 'Quelqu’un a besoin de vous',
  'push.concluiu': 'Un agent termine',
  'push.aparelhos': 'Appareils inscrits',
  'push.nenhum': 'Aucun appareil inscrit.',
  'push.remover': 'retirer',
  'push.este': 'cet appareil',
  'push.salvo': 'Enregistré.',
  // topo: contador de shells (5.1)
  'num.shells': 'shells en cours',
  'num.shells_dica': 'Cliquez pour aller au prochain agent avec un shell en cours',
  'sons.titulo': 'Sons',
  'sons.dica': 'Désactivés par défaut. Sons discrets, faits dans le navigateur. Onglet masqué : seule la cloche sonne.',
  'sons.volume': 'Volume',
  'sons.sino': 'Cloche : quelqu’un a besoin de vous',
  'sons.teclado': 'Clavier : ceux qui tapent dans les salles visibles',
  'sons.elevador': 'Ascenseur : quelqu’un arrive ou part',
  'sons.concluida': 'Pop : tâche terminée',
  'sons.rodas': 'Jeux : ping-pong, arcade, pierre-papier-ciseaux',
  'sons.fechar': 'Fermer',
  // demonstração pela interface (demo.ts)
  'demo.selo': 'démonstration',
  'demo.selo_titulo': 'Agents fictifs pour remplir le bureau. Ils ne comptent pas dans Ma journée.',
  'demo.ver': 'Voir la démonstration',
  'demo.faixa': 'agents fictifs à côté des vrais',
  'demo.desligar': 'Désactiver',
  'demo.erro': 'Impossible de changer la démonstration ({erro})',
  'demo.sem_resposta': 'pas de réponse du serveur',
  'demo.painel': 'Agent fictif, pas une vraie session.',
  // éditeur de personnage (ed.*) et le crayon du panneau
  'det.editar_personagem': 'Modifier le personnage',
  'ed.titulo_projeto': 'Personnage de l’agent principal de {projeto}',
  'ed.titulo_sessao': 'Personnage de {nome} ({projeto})',
  'ed.fechar': 'Fermer',
  'ed.previa': 'Aperçu du personnage',
  'ed.sortear': 'Tirer au sort',
  'ed.voltar_sorteio': 'Revenir au tirage',
  'ed.salvar': 'Enregistrer',
  'ed.salvando': 'Enregistrement…',
  'ed.cancelar': 'Annuler',
  'ed.salvo': 'Personnage enregistré.',
  'ed.erro': 'Impossible d’enregistrer\u00a0: {erro}',
  'ed.nome': 'Nom',
  'ed.nome_dica': 'De 1 à {max} lettres, sans reprendre le nom de quelqu’un du bureau, d’un autre personnage ou d’un autre projet.',
  'ed.nome_vazio': 'Écrivez un nom.',
  'ed.nome_longo': 'Utilisez jusqu’à {max} lettres.',
  'ed.nome_repetido': 'Ce nom est déjà utilisé.',
  'ed.grupo_corpo': 'Corps',
  'ed.grupo_pele': 'Peau',
  'ed.grupo_cabelo': 'Cheveux',
  'ed.grupo_cabelo_cor': 'Couleur des cheveux',
  'ed.grupo_barba': 'Barbe',
  'ed.grupo_olhos': 'Yeux',
  'ed.grupo_acessorio': 'Accessoire',
  'ed.grupo_acessorio_cor': 'Couleur de l’accessoire',
  'ed.grupo_cima': 'Haut',
  'ed.grupo_cima_cor': 'Couleur du haut',
  'ed.grupo_baixo': 'Bas',
  'ed.grupo_sapatos': 'Chaussures',
  'ed.nenhum': 'Aucun',
  'ed.cor': 'Couleur {n}',
  'ed.peca_dica': 'Le bureau dessine un accessoire par personnage\u00a0: lunettes, barbe et accessoire se remplacent.',
  'ed.editar_pecas': 'Modifier les pièces',
  'det.estatisticas': 'Statistiques',
  'stats.ferramentas': 'Outils',
  'stats.chamadas.one': '{n} appel',
  'stats.chamadas.other': '{n} appels',
  'stats.entrada': 'Entrée',
  'stats.saida': 'Sortie',
  'stats.custo': 'Coût',
  'stats.custo_sem': 'La CLI n’a pas enregistré le coût de cette session',
  'stats.custo_sub': 'somme des sessions qui indiquent le coût',
  'stats.linhas': 'Lignes modifiées',
  'stats.modelo': 'Modèle',
  'stats.branch': 'Branche',
  'stats.entrada_saida': 'entrée {e} · sortie {s}',
  'stats.desatualizado': 'obsolète',
  'stats.desatualizado_titulo': 'La lecture de la consommation date de plus de 30 min',
  'stats.sem_cota': 'pas de quota',
  'stats.sem_cota_titulo': 'Codex indique un crédit nul : pas de quota pour l’instant',
  'mdia.leg_alterna': 'Cliquez pour afficher ou masquer cet état dans les graphiques',
  'gh.t.pr-open': '🎉 a ouvert la PR{n}',
  'gh.t.pr-merge': '🎉 a fusionné la PR{n}',
  'gh.t.release': '🎉 a publié la release{tag}',
  'gh.t.ci-fail': '🚨 la CI a échoué{ramo}',
  'gh.t.ci-pass': '✅ CI au vert{ramo}',
  'gh.t.push': '⬆️ a poussé{ramo}',
  'gh.b.pr-open': 'PR{n} ouverte !',
  'gh.b.pr-merge': 'PR{n} fusionnée !',
  'gh.b.release': 'Release{tag} publiée !',
  'gh.b.ci-pass': 'CI au vert !',
  'gh.alarme': '🚨 La CI a échoué',
  'pty.titulo': 'Terminal interactif',
  'pty.nova': 'Nouvelle session',
  'pty.cli': 'CLI',
  'pty.conta': 'Compte',
  'pty.pasta': 'Dossier du projet',
  'pty.pedido': 'Première demande (facultatif)',
  'pty.criar': 'Ouvrir',
  'pty.encerrar': 'Terminer la session',
  'pty.confirmar': 'Cliquez encore pour terminer',
  'pty.assumir_confirma': 'Ceci TERMINE la session ouverte dans l’autre terminal et poursuit la même conversation ici. Continuer ?',
  'pty.vazio': 'Aucune session ouverte. Ouvrez-en une dans « Nouvelle session ».',
  'pty.conectando': 'Connexion…',
  'pty.terminou': '[le processus est terminé]',
  'pty.sem_pty': 'Le terminal interactif nécessite node-pty, qui n’est pas installé sur cette machine. Le terminal tmux reste disponible.',
  'pty.fechar_aba': 'Fermer',
  'pty.teclas': 'Touches',
  'pty.colar': 'Coller',
  'pty.aba_aria': 'Sessions du terminal interactif',
  'pty.sem_conta': 'Compte par défaut',
  'perm.sempre': 'Approuver et ne plus demander',
  'perm.interromper': "interrompre l'agent",
  'perm.interromper_dica': "Refuse et arrête l'agent maintenant, au lieu de refuser seulement cette action.",
  'msg.na_fila': 'en file',
  'msg.entregue': 'livré',
  'msg.nao_entregue': 'non livré : {motivo}',
  'ponte.codex_fila': "Codex n'a pas encore de file de messages dans cette version (la sous-commande queue n'existe pas), la boîte reste désactivée.",
  'balao.concluido': 'Terminé',
  // timelapse completo (7.2): velocidades, picos, replay e volta ao vivo (timelapse.ts)
  'tl.voltar': 'Retour en direct',
  'tl.replay': 'REPLAY',
  'tl.picos': 'Pics de la journée',
  'tl.pico': 'Pic à {h} ({n} en travail)',
  'tl.resumo': '{p} au campus · {n} au travail',
  'tl.legenda_presentes': 'présents',
  'tl.legenda_trabalho': 'au travail',
  'mdia.demo_aviso': "Chiffres fictifs : pas de travail réel, et rien n'est enregistré.",
  'roda.tv': 'TV',
  'roda.videogame': 'Jeu vidéo',
  'roda.fliperama': 'Borne d’arcade',
  'roda.pingpong': 'Ping-pong',
  'roda.papo': 'Pause café',
  'roda.corredor': 'Discussion au couloir',
  'roda.jokenpo': 'Pierre-feuille-ciseaux',
  'roda.espelho': 'Miroir des toilettes',
  'roda.prog.futebol': 'Football',
  'roda.prog.novela': 'Feuilleton',
  'roda.prog.desenho': 'Dessin animé',
  'roda.gol': '⚽ BUT !',
  'roda.fofoca.0': '🤫 Vu {nome} ?',
  'roda.fofoca.1': '🤭 {nome} a disparu…',
  'roda.fofoca.2': '👀 J’ai su pour {nome}…',
  'roda.fofoca.3': '😏 Et {nome}, alors ?',
  'traco.falante': 'Bavard',
  'traco.competitivo': 'Compétitif',
  'traco.caseiro': 'Casanier',
  'traco.gamer': 'Gamer',
  'traco.cinefilo': 'Cinéphile',
  'traco.fofoqueiro': 'Commère',
  'traco.atleta': 'Sportif',
  'traco.vaidoso': 'Coquet',
  'traco.cafeinado': 'Accro au café',
  'traco.brincalhao': 'Farceur',
  'traco.sossegado': 'Tranquille',
  'traco.retro': 'Rétro',
  'traco.sociavel': 'Sociable',
  'traco.curioso': 'Curieux',
  'roda.traco': 'Personnalité',
};

const DICIONARIOS: Record<Lang, Record<Chave, string>> = { pt: PT, en: EN, es: ES, fr: FR };

function ehIdioma(valor: unknown): valor is Lang {
  return valor === 'pt' || valor === 'en' || valor === 'es' || valor === 'fr';
}

/** Lê ?lang=, localStorage e navigator.language sem nunca lançar (modo privado, teste sem DOM). */
function detectar(): Lang {
  try {
    const url = new URLSearchParams(globalThis.location?.search ?? '').get('lang');
    if (ehIdioma(url)) return url;
  } catch {
    /* sem URL legível: segue para o próximo */
  }
  try {
    const salvo = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (ehIdioma(salvo)) return salvo;
  } catch {
    /* localStorage bloqueado: segue para o navegador */
  }
  // pt-BR, en-US, es-AR… viram o código base; qualquer outro idioma cai no português
  const base = globalThis.navigator?.language?.slice(0, 2).toLowerCase();
  return ehIdioma(base) ? base : 'pt';
}

let atual: Lang = detectar();
const ouvintes = new Set<(lang: Lang) => void>();

if (typeof document !== 'undefined') document.documentElement.lang = TAG[atual];

export function getLang(): Lang {
  return atual;
}

/** Código para toLocaleString (1.234 em pt, 1,234 em en). */
export function localeTag(): string {
  return TAG[atual];
}

/** Texto do dicionário do idioma atual; variáveis {nome} são trocadas. Chave ausente cai no pt. */
export function t(chave: Chave, vars?: Vars): string {
  const texto = DICIONARIOS[atual][chave] ?? PT[chave];
  if (!vars) return texto;
  return texto.replace(/\{(\w+)\}/g, (trecho, nome: string) => (nome in vars ? String(vars[nome]) : trecho));
}

/** Chave com sufixo .one / .other conforme n (1 = singular; em francês, 0 também pede singular). */
export function plural(base: string, n: number, vars?: Vars): string {
  const singular = n === 1 || (atual === 'fr' && n === 0);
  return t((singular ? `${base}.one` : `${base}.other`) as Chave, { ...vars, n });
}

/** A pessoa escolheu um idioma (?lang= ou seletor)? Então o padrão do CMS não manda. */
export function escolhaExplicita(): boolean {
  try {
    if (ehIdioma(new URLSearchParams(globalThis.location?.search ?? '').get('lang'))) return true;
  } catch {
    /* segue */
  }
  try {
    return ehIdioma(globalThis.localStorage?.getItem(STORAGE_KEY) ?? null);
  } catch {
    return false;
  }
}

/** `persistir: false` = idioma padrão vindo do CMS (não grava como escolha da pessoa). */
export function setLang(novo: Lang, persistir = true): void {
  if (!ehIdioma(novo)) return;
  atual = novo;
  if (persistir) {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, novo);
    } catch {
      /* sem persistência: vale só nesta aba */
    }
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = TAG[novo];
    translateDom();
  }
  for (const ouvinte of ouvintes) ouvinte(novo);
}

/** Quem precisa redesenhar com o idioma novo (a UI fica fora do alcance de translateDom). */
export function onLangChange(ouvinte: (lang: Lang) => void): void {
  ouvintes.add(ouvinte);
}

/**
 * Aplica o idioma aos elementos marcados no HTML:
 * data-i18n (texto), data-i18n-title, data-i18n-aria e data-i18n-placeholder.
 */
export function translateDom(raiz: ParentNode = document): void {
  raiz.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    el.textContent = t(el.dataset.i18n as Chave);
  });
  raiz.querySelectorAll<HTMLElement>('[data-i18n-title]').forEach((el) => {
    el.title = t(el.dataset.i18nTitle as Chave);
  });
  raiz.querySelectorAll<HTMLElement>('[data-i18n-aria]').forEach((el) => {
    el.setAttribute('aria-label', t(el.dataset.i18nAria as Chave));
  });
  raiz.querySelectorAll<HTMLElement>('[data-i18n-placeholder]').forEach((el) => {
    (el as HTMLInputElement).placeholder = t(el.dataset.i18nPlaceholder as Chave);
  });
}

/** Dicionários expostos só para os testes (checar que as três línguas têm as mesmas chaves). */
export const DICIONARIOS_PARA_TESTE = { pt: PT, en: EN, es: ES, fr: FR } as const;

// ------------------------------------------------------------------ atividades

/**
 * Os textos de atividade nascem no servidor, em português (src/adapters/common/describe.ts
 * e os adapters). Aqui eles são traduzidos por padrão no navegador: frase fixa ou prefixo +
 * resto (nome de arquivo, comando, pedido do dono), que fica como veio.
 */
const ACTIVITY_FIXED: Record<string, { en: string; es: string; fr: string }> = {
  'rodando um comando': { en: 'running a command', es: 'ejecutando un comando', fr: 'exécute une commande' },
  'organizando as tarefas': { en: 'organizing tasks', es: 'organizando las tareas', fr: 'organise les tâches' },
  'delegando para um subagente': { en: 'delegating to a subagent', es: 'delegando a un subagente', fr: 'délègue à un sous-agent' },
  'consultando a web': { en: 'browsing the web', es: 'consultando la web', fr: 'consulte le web' },
  'procurando no projeto': { en: 'searching the project', es: 'buscando en el proyecto', fr: 'cherche dans le projet' },
  'lendo um arquivo': { en: 'reading a file', es: 'leyendo un archivo', fr: 'lit un fichier' },
  'editando um arquivo': { en: 'editing a file', es: 'editando un archivo', fr: 'modifie un fichier' },
  'escrevendo um arquivo': { en: 'writing a file', es: 'escribiendo un archivo', fr: 'écrit un fichier' },
  'escrevendo a resposta': { en: 'writing the answer', es: 'escribiendo la respuesta', fr: 'rédige la réponse' },
  'pensando na resposta': { en: 'thinking about the answer', es: 'pensando la respuesta', fr: 'réfléchit à la réponse' },
  'esperando aprovação': { en: 'waiting for approval', es: 'esperando aprobación', fr: 'attend l’approbation' },
  'pediu uma tarefa': { en: 'asked for a task', es: 'pidió una tarea', fr: 'a demandé une tâche' },
  'usando uma ferramenta': { en: 'using a tool', es: 'usando una herramienta', fr: 'utilise un outil' },
  'a sessão deu erro': { en: 'the session failed', es: 'la sesión falló', fr: 'la session a échoué' },
  'esperando você': { en: 'waiting for you', es: 'esperándote', fr: 'vous attend' },
  'processo ativo, ocioso': { en: 'process alive, idle', es: 'proceso activo, inactivo', fr: 'processus actif, inactif' },
  trabalhando: { en: 'working', es: 'trabajando', fr: 'travaille' },
  escrevendo: { en: 'writing', es: 'escribiendo', fr: 'écrit' },
  pensando: { en: 'thinking', es: 'pensando', fr: 'réfléchit' },
  ocioso: { en: 'idle', es: 'inactivo', fr: 'inactif' },
};

const ACTIVITY_PREFIX: Array<[string, { en: string; es: string; fr: string }]> = [
  ['concluiu em ', { en: 'finished in ', es: 'terminó en ', fr: 'terminé en ' }],
  ['erro: limite de uso do modelo atingido', { en: 'error: model usage limit reached', es: 'error: límite de uso del modelo alcanzado', fr: 'erreur : limite d’usage du modèle atteinte' }],
  ['erro: ', { en: 'error: ', es: 'error: ', fr: 'erreur : ' }],
  ['pediu: ', { en: 'asked: ', es: 'pidió: ', fr: 'a demandé : ' }],
  ['você respondeu: ', { en: 'you answered: ', es: 'respondiste: ', fr: 'vous avez répondu : ' }],
  ['lendo ', { en: 'reading ', es: 'leyendo ', fr: 'lit ' }],
  ['editando ', { en: 'editing ', es: 'editando ', fr: 'modifie ' }],
  ['escrevendo ', { en: 'writing ', es: 'escribiendo ', fr: 'écrit ' }],
  ['procurando ', { en: 'searching ', es: 'buscando ', fr: 'cherche ' }],
  ['usando ', { en: 'using ', es: 'usando ', fr: 'utilise ' }],
];

const FAILED = { en: ' (failed)', es: ' (falló)', fr: ' (échoué)' };

/** Traduz um texto de atividade vindo do servidor para o idioma atual. */
export function tActivity(text: string): string {
  const lang = atual;
  if (lang === 'pt' || !text) return text;
  let rest = text;
  let suffix = '';
  if (rest.endsWith(' (falhou)')) {
    rest = rest.slice(0, -' (falhou)'.length);
    suffix = FAILED[lang];
  }
  const fixed = ACTIVITY_FIXED[rest];
  if (fixed) return fixed[lang] + suffix;
  for (const [pt, tr] of ACTIVITY_PREFIX) if (rest.startsWith(pt)) return tr[lang] + rest.slice(pt.length) + suffix;
  return text;
}

// ------------------------------------------------------------------ tempo e linha do tempo

/** Duração de um turno: '32s', '1min 32s', '1 h 5 min'. */
export function duracaoTurno(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return t('tempo.seg', { n: total });
  if (total < 3600) return t('tempo.min_seg', { m: Math.floor(total / 60), s: total % 60 });
  return t('tempo.h_min', { h: Math.floor(total / 3600), m: Math.floor((total % 3600) / 60) });
}

/** Há quanto tempo, desde `desde` (epoch ms): 'agora', 'há 12s', 'há 3 min', 'há 2 h', 'há 4 d'. */
export function tempoRelativo(desde: number, agora = Date.now()): string {
  const segundos = Math.max(0, Math.floor((agora - desde) / 1000));
  if (segundos < 10) return t('tempo.agora');
  let tempo: string;
  if (segundos < 60) tempo = t('tempo.seg', { n: segundos });
  else if (segundos < 3600) tempo = t('tempo.min', { n: Math.floor(segundos / 60) });
  else if (segundos < 86_400) tempo = t('tempo.h', { n: Math.floor(segundos / 3600) });
  else tempo = t('tempo.d', { n: Math.floor(segundos / 86_400) });
  return t('det.ha', { tempo });
}

/**
 * Ícone da linha do tempo, pelo tipo da atividade. `texto` é o da atividade como vem do servidor
 * (português): 'editando…' ganha ✏️. Em bash, comando de teste ganha 🧪.
 */
export function iconeDoKind(kind: string, texto = '', detalhe = ''): string {
  if (kind === 'bash' && /\b(test|tests|vitest|jest|pytest|mocha)\b|npm (run )?test/i.test(detalhe)) return '🧪';
  if (/^editando/i.test(texto)) return '✏️';
  switch (kind) {
    case 'read':
      return '📖';
    case 'write':
      return '📝';
    case 'search':
      return '🔎';
    case 'web':
      return '🌐';
    case 'thinking':
      return '💭';
    case 'reply':
      return '💬';
    case 'user':
      return '🙋';
    case 'error':
      return '⚠️';
    case 'task':
    case 'plan':
      return '📋';
    case 'bash':
      return '⚙️';
    case 'test':
      return '🧪';
    case 'install':
      return '📦';
    case 'build':
      return '🔨';
    case 'git':
      return '🔀';
    case 'github':
      return '🐙';
    case 'done':
      return '✅';
    default:
      return '•';
  }
}
